import { elementConfigurationProblems }
  from "%/dynamic-component-editor/helpers/element-configuration-problems";
import { defineStore } from "pinia";
import { isProvidedByAncestor } from "%/dynamic-component-editor/helpers/entity-binding-problems";
import { toRaw, watch } from "vue";
import { kebabToTitleCase, uuidv4 as generateUuidV4 } from "@bloglovin/vue-framework";
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import DynamicOption from "%/dynamic-component-editor/classes/dynamic-option";
import { Context } from "%/dynamic-component-editor/classes/context";
import StandardContextEntities from "%/dynamic-component-editor/classes/standard-context-entities";
import { breakpointAliasOf, flattenVisibleIdentifiers, identifiersBetween } from "%/dynamic-component-editor/helpers/element-range-selection";
import {
  requiredContextEntityType,
  bareRequiredContextEntityType,
  requiredContextValueType,
  isPrimitiveRequiredContext,
} from "%/dynamic-component-editor/helpers/required-context-entity-type";
import FeedComponentsMap from "%/dynamic-component-editor/classes/feed-components-map";
import RequiredContextValue from "%/dynamic-component-editor/classes/required-context-value";
import { ToastBuilder } from "@vcl";
import { partitionContextBindings } from "%/dynamic-component-editor/helpers/prune-context-bindings";
import { readInstanceContextBindings } from "%/dynamic-component-editor/helpers/context-bindings";
import { REFERENCED_COMPONENT_ID_KEY } from "%/dynamic-component-editor/helpers/component-reference-keys";
import { isAmbientContextName, readLegacyEntities } from "%/dynamic-component-editor/helpers/ambient-context";
import Clipboard, { PUBLICATION_CLIPBOARD_TYPE } from "%/dynamic-component-editor/classes/clipboard";
import App, { TEMP_PK_RESOLVED_EVENT } from "@bloglovin/inspire-engine/App";
import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import { clickInsertionPoint } from "%/dynamic-component-editor/helpers/click-insertion-point.mjs";
import { recordKeyAlias } from "%/dynamic-component-editor/utils/child-key-alias";
import SelectionBreakpointInvariantError from "%/dynamic-component-editor/errors/selection-breakpoint-invariant-error";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import useBuilderUndoStore from "%/dynamic-component-editor/stores/builder-undo-store";
import { useCanvasVisibilityPreviewStore } from "%/dynamic-component-editor/stores/canvas-visibility-preview-store";
import { onDrain, pendingMountJobs } from "%/dynamic-component-editor/utils/mount-scheduler";
import {
  readSelectionUrlState,
  writeSelectionUrlState,
  readBreakpointUrlState,
  writeBreakpointUrlState,
} from "%/dynamic-component-editor/utils/canvas-url-state";
import { readRequiredContexts, describeDynamicComponent } from "@studio/Helpers/studio-installed-apps-bootstrap";
import { builderPrimitiveValues } from "%/dynamic-component-editor/helpers/primitive-provides-context";
import { renameValueContextReferences } from "%/dynamic-component-editor/helpers/rename-context-bindings";
import StandardSurfaceSeeder from "%/dynamic-component-editor/classes/standard-surface-seeder";
import {
  readRequiredContextsFromRootOptions,
  requiresStoryContext,
  resolveCandidateStoryType,
  toStoryCardCandidate,
} from "%/dynamic-component-editor/helpers/story-card-candidates";
import { COMPLETION_STATE_ELEMENT_TYPES } from "%/dynamic-component-editor/helpers/completion-preview-states";
import { frameSurfaceWidth as computeFrameSurfaceWidth } from "%/dynamic-component-editor/helpers/frame-surface-width";
import { COMPLETION_CARD_OPTION_BY_STATUS } from "%/dynamic-component-editor/options/completion-card-variant";
import { boundComponentIdFromStored, componentBindingError, feedMapError }
  from "%/dynamic-component-editor/helpers/host-bound-component";
import {
  COMPLETION_ELEMENT_TYPES,
  COMPLETION_CARD_CONTEXT_BINDINGS_OPTION_KEY,
} from "%/dynamic-component-editor/constants/completion-card-option-keys";
import {
  completionPromptKey,
  stageCompletionContextBinding,
  pruneStateBindingsToRequired,
  pickedStateBindings,
} from "%/dynamic-component-editor/helpers/completion-card-context-staging";
import {
  unboundRequiredContexts,
  completionCardContextGaps,
  describeCompletionCardContextGap,
  completionCardContextGapsByState,
} from "%/dynamic-component-editor/helpers/completion-card-context-gaps";
import { planLegacyCompletionContextRepair }
  from "%/dynamic-component-editor/helpers/completion-card-context-repair";
import { useCompletionAssignmentStore } from "%/dynamic-component-editor/stores/completion-assignment-store";
import BaseDropSelection from "%/dynamic-component-editor/classes/base-drop-selection";
import "%/dynamic-component-editor/classes/drop-selections/linked-component-selection";
import { standardSurfaceKeyOf } from "%/dynamic-component-editor/helpers/standard-component-suggestion.mjs";
import "%/dynamic-component-editor/classes/drop-selections/resume-card-selection";
import FeedRowContextGate from "%/dynamic-component-editor/classes/feed-row-context-gate";
import ContextRegistryScope from "%/dynamic-component-editor/classes/context-registry-scope";
import { variantGroupMembers } from "%/dynamic-component-editor/helpers/destination-variant-groups";
import { destinationElementsOf } from "%/dynamic-component-editor/helpers/dynamic-component-elements";
import { LIBRARY_DRAG_TYPE, libraryTreeForInsert } from "%/dynamic-component-editor/helpers/section-library-drop";
import { useStudioSectionLibraryStore } from "@studio/Stores/studio-section-library-store";
import { builderPageFor } from "%/dynamic-component-editor/helpers/expression-globals";
import { registerBuilderPageReader } from "%/dynamic-component-editor/helpers/expression-global-values";

function reconcileSet(set, desired) {
  const want = desired instanceof Set ? desired : new Set(desired);
  for (const key of set) {
    if (!want.has(key)) {
      set.delete(key);
    }
  }
  for (const key of want) {
    if (!set.has(key)) {
      set.add(key);
    }
  }
}

function bareEntityType(value) {
  return typeof value === "string" ? value.replace(/^Legacy/, "") : value;
}

function parseContextBinding(options) {
  let tree = options;
  if (typeof tree === "string") {
    try {
      tree = JSON.parse(tree);
    } catch {
      return null;
    }
  }
  const ctx = tree?.context;
  return ctx && typeof ctx === "object" && !Array.isArray(ctx) && ctx.entityContext ? ctx : null;
}

function referencedComponentIdFromOptions(mergedOptions) {
  const selectionClass = BaseDropSelection.forElementType("DynamicComponentElement");
  return selectionClass ? selectionClass.valueFromOptions(mergedOptions) : 0;
}

function dropSelectionValueForRow(values, row, rows) {
  if (values !== null && typeof values === "object") {
    return values[row.optionKey];
  }
  return rows.length === 1 ? values : undefined;
}

function bindingValueFor(entry) {
  const data = entry.data;
  if (RequiredContextValue.isPrimitiveBinding(data)) {
    return data;
  }
  if (data.entitySource === "context" && !data.sourceField) {
    return data.sourceContext;
  }
  return data;
}

const LEGACY_FLAT_CONTEXT_BINDINGS_OPTION_KEY = "context-bindings";

function resolveDropSourceElement(sourceElementId) {
  if (sourceElementId === null || sourceElementId === undefined) {
    return null;
  }
  const candidateIds = [sourceElementId];
  if (Number(sourceElementId) >= 1e10) {
    const realPk = App.get("CreatorStudio").resolveTempPk("Element", { id: Number(sourceElementId) });
    if (realPk?.id !== undefined && realPk?.id !== null) {
      candidateIds.unshift(realPk.id);
    }
  }
  for (const candidateId of candidateIds) {
    const element = DynamicElement.get(String(candidateId));
    if (element?.element) {
      return element;
    }
  }
  return null;
}

const UNDOABLE_EVENTS = new Set([
  "AddElement",
  "UpdateElement",
  "MoveElement",
  "MoveElements",
  "DeleteElement",
  "DeleteElements",
  "DuplicateElement",
  "DuplicateElements",
  "WrapElements",
  "UpdateDynamicComponentRootOptions",
]);

const VERSION_SNAPSHOT_DEBOUNCE_MS = 15000;

const TEMP_PK_MINIMUM = 1e10;
const MAX_ANCESTOR_DEPTH = 64;
const CREATED_COMPONENT_PK_POLL_MS = 250;
const CREATED_COMPONENT_PK_ATTEMPTS = 60;

const WRAP_RECONCILE_TIMEOUT_MS = 10000;

const BULK_OPTION_EDIT_COALESCE_MS = 800;

const TEMP_PK_FLOOR = 1e10;

const DUPLICATE_APP_COMPONENT_PARAM = "duplicateAppComponent";
const DUPLICATE_HYDRATION_POLL_MS = 250;
const DUPLICATE_HYDRATION_SETTLE_MS = 1500;
const DUPLICATE_HYDRATION_TIMEOUT_MS = 30000;

function parseRootOptions(value) {
  if (value && typeof value === "object") {
    return value;
  }
  try {
    const parsed = JSON.parse(value || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    throw new DynamicComponentError("Cannot duplicate: the source component's root options are not valid JSON.", {
      rootOptions: String(value).slice(0, 200),
    });
  }
}

function setDragImageUnlessRefused(dataTransfer, image, offsetX, offsetY) {
  try {
    dataTransfer.setDragImage(image, offsetX, offsetY);
    return true;
  } catch {
    return false;
  }
}

export const useDynamicComponentEditorStore = defineStore("dynamic-component-editor-store", {
  state: () => ({
    contextScope: null,
    brokenReferencesByElementId: {},
    // XXX: Errors caught by the per-element boundary on the canvas, keyed by
    renderErrorsByElementId: {},
    dynamicComponentId: null,
    breakpointPreviewWidths: {},
    variants: {},
    baseUrl: "",
    _breakpointsProvider: {
      getAvailableBreakpoints: () => [],
      getHiddenBreakpointAliases: () => [],
      onBreakpointsChanged: null,
    },
    activeBreakpointAlias: "default",
    hasPermissionToEdit: false,
    hideBreakpoints: false,
    rootElementIdentifier: null,
    optionState: "default",
    inspectorTabRequest: null,
    activeCanvasPreviewCount: 0,
    contexts: {},
    pendingContextPrompts: [],
    activeFeedComponentPrompt: null,
    activeStoryCardPrompt: null,
    pendingStoryCardPickerAfterPrompts: null,
    activeCompletionCardPrompt: null,
    activeDropSelection: null,
    elementTreeMutationEpoch: 0,
    pages: {},
    events: {},
    entities: {},
    components: {},
    canvasRef: null,
    currentlySelectedElementIds: [],
    selectionAnchorIdentifier: null,
    layersPanelSelectedIdentifier: null,
    selectionSet: new Set(),
    focusRevision: 0,
    hoverSet: new Set(),
    focusBareSet: new Set(),
    parentsOfFocusBareSet: new Set(),
    draggingBareSet: new Set(),
    _undoSelectionBefore: null,
    _undoSinkRegistered: false,
    _lastKnownCurrentVersionId: null,
    wasRepointedExternally: false,
    _suppressUndoCapture: false,
    _versionSnapshotTimer: null,
    _bulkOptionEdit: null,
    currentlyHoveringOverElementId: null,
    gpuOverlayActive: false,
    gpuHitTest: null,
    activeSnapGuides: [],
    layoutInspectorActive: false,
    layersCollapsed: {},
    canvasOffsetX: 0,
    canvasOffsetY: 0,
    zoom: 1,
    isCanvasGestureActive: false,
    isDragging: false,
    isPastingElements: false,
    currentlyDraggingElement: null,
    dropTarget: { containerIdentifier: null, index: 0 },
    indicatorIsInLayoutWithIdentifier: null,
    draggingOverHoverState: { timerId: null, targetId: null },
    isDraggingOnLayersPalette: false,
    layersPanelExpandElementId: null,
    dropTargetElementIdentifier: null,
    editingElementsInline: [],
    detachingElementIdentifier: null,
    doubleClickedIdentifier: null,
    pendingComponentRenamePkString: null,
    pendingComponentRenameDraft: null,
    queuedComponentRenames: {},
    showVersionMismatchModal: false,
    showInvalidCloneModal: false,
    invalidCloneModalMessage: "",
    invalidCloneElementTypes: [],
    iOwnThisDynamicComponent: false,
    dynamicComponentHasAnUpdateFromParent: false,
    isInspectorPaletteOpen: true,
    widthOfTheInspectorPalette: 300,
    collapseStateOfElementGroups: {},
    elementsDrillPath: [],

    canEditPublication: true,
    childrenToFocus: [],
    dynamicComponentName: "",
    elementsToFocus: [],
    isDraggingOnBuilder: false,
    isDynamicAppComponent: false,
    newDynamicComponentLayout: null,
    parentsToFocus: [],
    showContextualMenuForIdInBuilder: null,
    showContextualMenuForIdInLayers: null,
    contextualMenuPosition: { top: 0, left: 0 },
    showIdentifiersInToolbar: false,
  }),

  getters: {
    publicationIdForRequests() {
      const fromInstance = Number(this.dynamicComponentInstance?.destinationId) || 0;
      if (fromInstance) {
        return fromInstance;
      }
      const match = (typeof window === "undefined" ? "" : window.location.pathname)
          .match(/\/(?:build|staging|destination)(?:studio)?\/(\d+)/);
      return match ? Number(match[1]) : 0;
    },
    elementBrokenReferences() {
      return this.brokenReferencesByElementId;
    },
    // XXX: ONE walk of the element tree answering BOTH validity questions. They
    // XXX: were separate getters walking the same tree twice -- an artifact of two
    // XXX: PRs landing independently, reconciled here rather than left duplicated.
    // XXX: The questions stay distinct because they are distinct:
    // XXX:   unrenderable -> "will this element resolve once compiled?"
    // XXX:   problems     -> "is this element configured properly?"
    // XXX: Both feed the SAME errorsForElement call, so dropping either stops one
    // XXX: class of breakage painting the layers row red.
    elementValidityScan() {
      const dc = this.dynamicComponentInstance;
      if (!dc?.rootElementId) {
        return { unrenderable: {}, problems: {} };
      }
      // XXX: Read the context registry's revision DIRECTLY. This scan is only
      void Context.sourceRevision;
      const lost = dc.elementTypesLostOnCompile;
      const contextEntities = this.allAvailableContextEntities;
      const unrenderable = {};
      const problems = {};
      const stack = [dc.rootElementId];
      while (stack.length) {
        const element = DynamicElement.get(stack.pop());
        if (!element?.element) {
          continue;
        }
        element.childElementIds.forEach(childId => stack.push(childId));
        if (lost?.size && lost.has(element.type)) {
          unrenderable[element.element.id] = element.type;
        }
        const found = [
          ...elementConfigurationProblems(element, dc, contextEntities),
          ...this.embeddedComponentProblem(element),
        ];
        if (found.length) {
          problems[element.element.id] = found;
        }
      }
      Object.entries(this.renderErrorsByElementId).forEach(([elementId, message]) => {
        const existing = problems[elementId] ?? [];
        problems[elementId] = [...existing, message];
      });
      return { unrenderable, problems };
    },
    // XXX: An element that RENDERS ANOTHER DC must go red when that DC is broken,
    // XXX: or the author sees a healthy-looking page whose header is a broken
    // XXX: block they cannot account for (owner ruling 2026-08-29). The embedded
    // XXX: DC is judged on ITS OWN declared contexts -- the contract it was
    // XXX: authored against -- not the embedding page's, because a page may
    // XXX: legitimately satisfy the same context name with a different entity.
    // XXX: One level deep by design: the embedded DC's own host elements report
    // XXX: their own embeds when the author opens it, and an unbounded walk over
    // XXX: a cyclic embed graph hangs the browser (see task #86).
    embeddedComponentProblem() {
      const contextEntities = this.allAvailableContextEntities;
      return element => {
        const referenced = element.getRawOptionValue?.("referenced-component-id", "default", "default")?.r;
        const referencedId = Number(referenced) || 0;
        if (referencedId <= 0) {
          return [];
        }
        const embedded = DynamicComponent.get(referencedId, false);
        if (!embedded?.rootElementId) {
          return [];
        }
        let brokenCount = 0;
        const stack = [embedded.rootElementId];
        const seen = new Set();
        while (stack.length) {
          const id = String(stack.pop());
          if (seen.has(id)) {
            continue;
          }
          seen.add(id);
          const child = DynamicElement.get(id);
          if (!child?.element) {
            continue;
          }
          child.childElementIds.forEach(childId => stack.push(childId));
          if (elementConfigurationProblems(child, embedded, contextEntities).length) {
            brokenCount += 1;
          }
        }
        if (!brokenCount) {
          return [];
        }
        const label = embedded.name ? `"${embedded.name}"` : `component ${referencedId}`;
        return [
          `The component this element renders, ${label}, cannot render: `
          + `${brokenCount} of its elements ${brokenCount === 1 ? "is" : "are"} broken. `
          + "Open that component to fix it.",
        ];
      };
    },
    elementsUnrenderableOnCompiledSurface() {
      return this.elementValidityScan.unrenderable;
    },
    elementConfigurationProblems() {
      return this.elementValidityScan.problems;
    },
    // XXX: THE builder's source of "this element is misconfigured". The canvas
    // XXX: broken-state block and the layers-panel row colour BOTH read this, so
    // XXX: they cannot disagree. They used to compute independently, which is how
    // XXX: a feed painted a red card on canvas while its layers row stayed white.
    elementMisconfigurations() {
      const dc = this.dynamicComponentInstance;
      if (!dc?.rootElementId) {
        return {};
      }
      const dcId = dc.id ?? "unknown";
      const reasons = {};
      const stack = [dc.rootElementId];
      while (stack.length) {
        const element = DynamicElement.get(stack.pop());
        if (!element?.element) {
          continue;
        }
        element.childElementIds.forEach(childId => stack.push(childId));
        const reason = componentBindingError(element, dcId) || feedMapError(element);
        if (reason) {
          reasons[element.element.id] = reason;
        }
      }
      return reasons;
    },
    elementContextErrors() {
      const dc = this.dynamicComponentInstance;
      if (!dc?.rootElementId) {
        return {};
      }
      const declared = new Set((dc.availableContextsEntities ?? []).map(context => context.name));
      const legacyEntities = readLegacyEntities();
      const isEntityType = name => isAmbientContextName(name, legacyEntities);

      const errors = {};
      const stack = [dc.rootElementId];
      while (stack.length) {
        const element = DynamicElement.get(stack.pop());
        if (!element?.element) {
          continue;
        }
        element.childElementIds.forEach(childId => stack.push(childId));

        const boundContext = element.parsedOptions?.context?.entityContext;
        if (boundContext && !declared.has(boundContext) && !isEntityType(boundContext)) {
          errors[element.element.id] = boundContext;
          continue;
        }
        const required = this.readElementRequiredContexts(element);
        const bindings = element.getRawOptionValue("context-bindings", "default", "default")?.r;
        const unsatisfied = this.embedUnsatisfiedContexts(required, bindings);
        if (unsatisfied.length) {
          errors[element.element.id] = unsatisfied[0].name;
        }
      }
      return errors;
    },
    activeBreakpoint() {
      return this.selectableBreakpoints.find(b => b.alias === this.activeBreakpointAlias)
        ?? this.selectableBreakpoints[0];
    },
    dynamicComponentInstance() {
      return this.dynamicComponentId ? DynamicComponent.get(this.dynamicComponentId) : null;
    },
    liveCurrentVersionId() {
      if (!this.dynamicComponentId) {
        return null;
      }
      const app = App.get("CreatorStudio");
      const field = app.getEntityField("DynamicComponent", { id: String(this.dynamicComponentId) }, "currentVersion");
      const id = Number(field?.pk?.id ?? field?.id ?? field);
      return Number.isFinite(id) ? id : null;
    },
    dynamicComponentClass() {
      return this.dynamicComponentInstance?.class;
    },
    frameSurfaceWidth() {
      return alias => computeFrameSurfaceWidth({
        alias,
        previewWidths: this.breakpointPreviewWidths,
        breakpoints: this.availableBreakpoints,
        designVariant: useConfigurationStore?.()?.designVariant ?? "desktop",
      });
    },
    availableBreakpoints() {
      const raw = this._breakpointsProvider.getAvailableBreakpoints?.() ?? [];
      const designVariant = useConfigurationStore?.()?.designVariant ?? "desktop";
      const ascending = designVariant === "mobile";
      return [...raw].sort((a, b) => {
        const av = parseInt(a?.value, 10);
        const bv = parseInt(b?.value, 10);
        const aFinite = Number.isFinite(av);
        const bFinite = Number.isFinite(bv);
        if (!aFinite && !bFinite) {
          return 0;
        }
        if (!aFinite) {
          return 1;
        }
        if (!bFinite) {
          return -1;
        }
        return ascending ? av - bv : bv - av;
      });
    },
    hiddenBreakpointAliases() {
      return this._breakpointsProvider.getHiddenBreakpointAliases?.() ?? [];
    },
    selectableBreakpoints() {
      return [{ value: "", label: "Default", alias: "default" }, ...this.availableBreakpoints];
    },
    draggingCanvasElementBareId() {
      const dragging = this.currentlyDraggingElement;
      if (!dragging || !this.isDragging) {
        return null;
      }
      const identifier = dragging.identifier ?? dragging.id ?? null;
      if (identifier == null) {
        return null;
      }
      return String(identifier).split(":")[0];
    },
    canvasDropIsNoop() {
      const draggingBare = this.draggingCanvasElementBareId;
      if (!draggingBare) {
        return false;
      }
      const containerIdentifier = this.dropTarget?.containerIdentifier;
      if (!containerIdentifier) {
        return false;
      }
      const containerBare = String(containerIdentifier).split(":")[0];
      const draggingInst = DynamicElement.get(draggingBare);
      const draggingParentId = draggingInst?.parentElementId;
      if (draggingParentId == null || String(draggingParentId) !== containerBare) {
        return false;
      }
      const siblings = DynamicElement.get(containerBare)?.childElementIds ?? [];
      const ownIndex = siblings.indexOf(String(draggingBare));
      if (ownIndex === -1) {
        return false;
      }
      const dropIndex = this.dropTarget.index;
      return dropIndex === ownIndex || dropIndex === ownIndex + 1;
    },
    rootElementIsSelected() {
      return this.currentlySelectedElementIds.includes(this.rootElementIdentifier);
    },
    themeId() {
      return this.dynamicComponentInstance?.themeId;
    },
    themeMode() {
      return this.dynamicComponentInstance?.themeMode;
    },
    currentlyInspectedElements() {
      return this.currentlySelectedElementIds.map(id => DynamicElement.get(id)).filter(Boolean);
    },
    shareableSelectionBareIds() {
      const bareIds = [];
      this.currentlySelectedElementIds.forEach(id => {
        const bareId = String(id).split(":")[0];
        if (bareIds.includes(bareId)) {
          return;
        }
        if (DynamicElement.get(bareId)?.element?.isEntityPkTemp?.() === true) {
          return;
        }
        bareIds.push(bareId);
      });
      return bareIds;
    },
    selectionSharesSameParent() {
      const ids = this.currentlySelectedElementIds;
      if (ids.length === 0) {
        return false;
      }
      const parents = ids.map(id => DynamicElement.get(id)?.parentElementId ?? null);
      return parents.every(parentId => parentId != null && parentId === parents[0]);
    },
    declaredContextTypes() {
      const dc = this.dynamicComponentInstance;
      if (!dc) {
        return [];
      }
      const read = map => Object.entries(map ?? {})
          .filter(([, entry]) => entry && !entry.referred && !isPrimitiveRequiredContext(entry))
          .map(([name, entry]) => ({
            name,
            entityType: bareEntityType(entry.entity ?? entry.type ?? entry.entityType ?? null),
          }))
          .filter(candidate => candidate.entityType);
      return [
        ...read(dc.getRootOption("required-contexts")),
        ...read(dc.getRootOption("provides-contexts")),
      ];
    },
    inspectedElementContextBinding() {
      const elements = this.currentlyInspectedElements;
      if (elements.length !== 1) {
        return null;
      }
      const binding = elements[0].entityBinding;
      if (!binding) {
        return null;
      }
      if (binding.source === "options"
          && isAmbientContextName(binding.contextName, readLegacyEntities())) {
        return null;
      }
      const alias = binding.contextName;
      const declared = this.declaredContextTypes;
      const bound = declared.find(candidate => candidate.name === alias)
        ?? (isProvidedByAncestor(elements[0], alias) ? { name: alias, entityType: binding.entityType ?? null } : null);
      const entityType = binding.entityType ?? bound?.entityType ?? null;
      const isWrongType = Boolean(bound && entityType && bound.entityType !== entityType);
      const matching = entityType
        ? declared.filter(candidate => candidate.entityType === entityType)
        : declared;
      return {
        alias: typeof alias === "string" ? alias : "",
        entityType,
        field: binding.field ?? null,
        isMissing: !bound,
        isWrongType,
        candidates: matching.map(candidate => ({ value: candidate.name, label: candidate.name })),
      };
    },
    isTemplateType() {
      return !!this.dynamicComponentInstance?.getRootOption("template-type");
    },
    isTemplateOriginal() {
      return !this.dynamicComponentInstance?.getRootOption("original-template-type");
    },
    isPage() {
      return ["standard-page", "custom-page", "page"].includes(this.dynamicComponentInstance?.type);
    },
    isComponent() {
      return this.dynamicComponentInstance?.type === "component";
    },

    activeState() {
      const state = this.optionState || "default";
      const aliasRaw = this.activeBreakpointAlias;
      const alias = typeof aliasRaw === "string" && !aliasRaw.startsWith("[object ")
        ? aliasRaw
        : "default";
      const bp = alias && alias !== "default" ? alias : "";
      return bp ? `${state}:${bp}` : state;
    },

    expressionBuilderPage() {
      const dc = this.dynamicComponentInstance;
      if (!dc) {
        return null;
      }
      const studio = App.get("CreatorStudio");
      return builderPageFor({
        dynamicComponentId: Number(dc.id),
        isPage: this.isPage,
        canonicalId: this.canonicalVariantIdForComponent(dc.id),
        readName: id => {
          if (Number(id) === Number(dc.id)) {
            return dc.name;
          }
          return studio.getEntityField("DynamicComponent", { id: String(id) }, "name");
        },
      });
    },
    builderPrimitiveValueContexts() {
      const dc = this.dynamicComponentInstance;
      if (!dc) {
        return {};
      }
      return builderPrimitiveValues(dc.getRootOption("provides-contexts"), `Page "${describeDynamicComponent(dc)}"`);
    },
    contextItems() {
      const items = { ...Context.allContexts };
      const dc = this.dynamicComponentInstance;
      if (!dc) {
        return items;
      }
      const declared = {
        ...dc.getRootOption("required-contexts") ?? {},
        ...dc.getRootOption("provides-contexts") ?? {},
      };
      Object.entries(declared).forEach(([name, entry]) => {
        if (!entry || entry.referred) {
          return;
        }
        if (isPrimitiveRequiredContext(entry)) {
          return;
        }
        let entityPkUrlVars = entry.entityPkUrlVars ?? {};
        if (entry.entitySource === "constant") {
          entityPkUrlVars = entry.entityPkVars ?? entry.entityPkUrlVars ?? {};
        } else if (entry.entitySource === "standard" && entry.standardEntity === "currentUser") {
          const sessionUserId = Number(window.blclient?.data?.inspirationUserId) || 0;
          entityPkUrlVars = sessionUserId ? { id: sessionUserId } : {};
        }
        items[name] = {
          ...entry,
          entityType: requiredContextEntityType(entry),
          entityPkUrlVars,
        };
      });
      return items;
    },
    dropSelectionClass() {
      const elementType = this.activeDropSelection?.elementType;
      if (!elementType) {
        return null;
      }
      return BaseDropSelection.forDrop(elementType, this.activeDropSelection?.elementDef);
    },

    dropSelectionPromptText() {
      const selectionClass = this.dropSelectionClass;
      if (!selectionClass) {
        return "";
      }
      if (typeof selectionClass.promptTextFor !== "function") {
        return selectionClass.promptText ?? "";
      }
      return selectionClass.promptTextFor(
        this.activeDropSelection?.elementDef,
        this.activeDropSelection?.elementType ?? "",
      );
    },
    dropSelectionSuggestedValues() {
      const selectionClass = this.dropSelectionClass;
      if (!selectionClass || this.activeDropSelection?.trigger === "edit") {
        return {};
      }
      return selectionClass.suggestedValues(this, this.activeDropSelection?.elementDef, this.activeDropSelection?.elementType ?? "");
    },
    dropSelectionCandidates() {
      const selectionClass = this.dropSelectionClass;
      return selectionClass ? selectionClass.candidates(this) : [];
    },
    authoredComponentCandidates() {
      const { isComponentRowVisible } =
        require("%/dynamic-component-editor/helpers/component-row-visibility");
      return this.referencedComponentCandidates.filter(candidate => isComponentRowVisible({
        appName: "CreatorStudio",
        componentPk: { id: String(candidate.id) },
      }));
    },
    referencedComponentCandidates() {
      const destinationId = this.dynamicComponentInstance?.destinationId;
      if (!destinationId) {
        return [];
      }
      const studio = App.get("CreatorStudio");
      const destinationPk = { id: String(destinationId) };
      const meta = studio.getFeedMeta("Destination", destinationPk, "components", "hasLoadedAtLeastOnce");
      if (!meta) {
        studio.triggerFeedInitialLoad("Destination", destinationPk, "components");
      }
      const items = studio.getFeedItems("Destination", destinationPk, "components") ?? [];
      const candidates = [];
      items.forEach(item => {
        const componentId = item?.entityPk?.id;
        if (!componentId) {
          return;
        }
        const name = studio.getEntityField("DynamicComponent", { id: String(componentId) }, "name");
        const rootOptions = studio.getEntityField("DynamicComponent", { id: String(componentId) }, "rootOptions");
        candidates.push({
          id: Number(componentId),
          name: name || `Component ${componentId}`,
          standardSurfaceKey: standardSurfaceKeyOf(rootOptions, componentId),
        });
      });
      return candidates;
    },
    storyCardCandidates() {
      const destinationId = this.dynamicComponentInstance?.destinationId;
      if (!destinationId) {
        return [];
      }
      const studio = App.get("CreatorStudio");
      const destinationPk = { id: String(destinationId) };
      const meta = studio.getFeedMeta("Destination", destinationPk, "components", "hasLoadedAtLeastOnce");
      if (!meta) {
        studio.triggerFeedInitialLoad("Destination", destinationPk, "components");
      }
      const items = studio.getFeedItems("Destination", destinationPk, "components") ?? [];
      const candidates = [];
      items.forEach(item => {
        if (!item.entityPk || Number(item.entityPk.id) >= 1e10) {
          return;
        }
        if (studio.getEntityField("DynamicComponent", item.entityPk, "status") === "inactive") {
          return;
        }
        const name = studio.getEntityField("DynamicComponent", item.entityPk, "name");
        const rootOptions = studio.getEntityField("DynamicComponent", item.entityPk, "rootOptions") ?? {};
        if (StandardSurfaceSeeder.standardClassOf({ name, rootOptions })) {
          return;
        }
        const storyType = resolveCandidateStoryType(rootOptions, name);
        if (!storyType && !requiresStoryContext(readRequiredContextsFromRootOptions(rootOptions))) {
          return;
        }
        const candidate = toStoryCardCandidate(item.entityPk.id, name, storyType);
        if (candidate) {
          candidates.push(candidate);
        }
      });
      return candidates;
    },
    componentNameById() {
      const destinationId = this.dynamicComponentInstance?.destinationId;
      if (!destinationId) {
        return {};
      }
      const studio = App.get("CreatorStudio");
      const destinationPk = { id: String(destinationId) };
      const meta = studio.getFeedMeta("Destination", destinationPk, "components", "hasLoadedAtLeastOnce");
      if (!meta) {
        studio.triggerFeedInitialLoad("Destination", destinationPk, "components");
      }
      const items = studio.getFeedItems("Destination", destinationPk, "components") ?? [];
      const map = {};
      items.forEach(item => {
        if (!item.entityPk) {
          return;
        }
        const name = studio.getEntityField("DynamicComponent", item.entityPk, "name");
        if (name) {
          map[Number(item.entityPk.id)] = name;
        }
      });
      return map;
    },
    resolveComponentName() {
      return id => {
        const key = Number(id);
        if (!key) {
          return "";
        }
        const fromFeed = this.componentNameById[key];
        if (fromFeed) {
          return fromFeed;
        }
        const name = App.get("CreatorStudio").getEntityField("DynamicComponent", { id: String(key) }, "name");
        return name || `Component ${key}`;
      };
    },
    appComponentCopyIndex() {
      const destinationId = this.dynamicComponentInstance?.destinationId;
      if (!destinationId) {
        return {};
      }
      const studio = App.get("CreatorStudio");
      const destinationPk = { id: String(destinationId) };
      const meta = studio.getFeedMeta("Destination", destinationPk, "components", "hasLoadedAtLeastOnce");
      if (!meta) {
        studio.triggerFeedInitialLoad("Destination", destinationPk, "components");
      }
      const items = studio.getFeedItems("Destination", destinationPk, "components") ?? [];
      const index = {};
      items.forEach(item => {
        if (!item.entityPk || Number(item.entityPk.id) >= 1e10) {
          return;
        }
        const ownerApp = studio.getEntityField("DynamicComponent", item.entityPk, "appName");
        if (!ownerApp) {
          return;
        }
        if (studio.getEntityField("DynamicComponent", item.entityPk, "status") === "inactive") {
          return;
        }
        const rootOptions = studio.getEntityField("DynamicComponent", item.entityPk, "rootOptions") ?? {};
        let parsed = rootOptions;
        if (typeof rootOptions === "string") {
          try {
            parsed = JSON.parse(rootOptions);
          } catch {
            parsed = {};
          }
        }
        const sourceComponent = parsed?.["app-source-component"];
        if (sourceComponent) {
          index[`${ownerApp}:${sourceComponent}`] = Number(item.entityPk.id);
        }
      });
      return index;
    },
    requiredContextPromptRows() {
      return required => {
        return Object.entries(required ?? {})
            .map(([name, def]) => ({
              name,
              entityType: bareRequiredContextEntityType(def),
              valueType: requiredContextValueType(def),
              appName: def.appName ?? null,
            }));
      };
    },
    unsatisfiedRequiredContexts() {
      return (required, bindings = {}) => {
        const items = this.contextItems;
        return this.requiredContextPromptRows(required).filter(row => {
          const def = (required ?? {})[row.name];
          if (def && Object.prototype.hasOwnProperty.call(def, "default")) {
            return false;
          }
          if (requiredContextValueType(def)) {
            return !RequiredContextValue.isPrimitiveBinding(bindings?.[row.name]);
          }
          const binding = bindings?.[row.name];
          if (RequiredContextValue.isEntityBinding(binding)) {
            return false;
          }
          const sourceName = typeof binding === "string" && binding ? binding : row.name;
          const available = items[sourceName];
          if (!available) {
            return true;
          }
          return Boolean(row.entityType) && bareEntityType(available.entityType) !== row.entityType;
        });
      };
    },
    embedUnsatisfiedContexts() {
      return (required, rawBindings) => this.unsatisfiedRequiredContexts(
        required,
        readInstanceContextBindings(rawBindings),
      );
    },
    pageUriVars() {
      const slugTemplate = this.dynamicComponentInstance?.getRootOption("page-slug") ?? "";
      return [...String(slugTemplate).matchAll(/\{([^}]+)\}/g)]
          .map(match => ({ value: match[1], label: match[1] }));
    },
    activeContextPromptBatch() {
      return this.pendingContextPrompts;
    },
    feedComponentPromptBatch() {
      const prompt = this.activeFeedComponentPrompt;
      if (!prompt) {
        return [];
      }
      return FeedRowContextGate.promptRows({
        requiredContexts: prompt.requiredContexts,
        rowEntityType: prompt.rowEntityType,
        contextVarName: prompt.contextVarName,
      });
    },
    feedItemContextCandidate() {
      const prompt = this.activeFeedComponentPrompt;
      if (!prompt?.contextVarName || !prompt?.rowEntityType) {
        return null;
      }
      const anchorDef = prompt.requiredContexts?.[prompt.contextVarName];
      const anchorType = anchorDef?.entity ?? anchorDef?.type ?? null;
      if (anchorType && bareEntityType(anchorType) !== bareEntityType(prompt.rowEntityType)) {
        return null;
      }
      return {
        name: prompt.contextVarName,
        entityType: prompt.rowEntityType,
        appName: prompt.rowAppName ?? undefined,
        isFeedItem: true,
      };
    },
    allAvailableContextEntities() {
      const apps = Context.allSources ?? {};
      return Object.fromEntries(
        Object.entries(apps).flatMap(([appName, app]) => {
          const entities = app?.entities ?? {};
          return Object.entries(entities).map(([key, item]) => [
            key,
            { ...item, key, appName },
          ]);
        }),
      );
    },
    allContextEntityConfigsByApp() {
      const sources = Context.allSources ?? {};
      return Object.fromEntries(
        Object.entries(sources).map(([appName, source]) => [
          appName,
          {
            title: source?.title ?? source?.displayName ?? appName,
            entities: source?.entities ?? {},
            events: source?.events ?? {},
          },
        ]),
      );
    },
    availableStandardContextEntities() {
      const appSources = Object.fromEntries(
        Object.entries(Context.allSources ?? {}).filter(([, source]) => source?.appName),
      );
      return StandardContextEntities.enumerate({
        sources: appSources,
        catalog: window.blclient?.data?.dbApps ?? {},
        allow: this.contextScope?.session?.map(row => row.value) ?? null,
      });
    },
    standardContextCandidates() {
      return Object.entries(Context.allContexts ?? {})
          .filter(([, entry]) => entry?.entitySource === "standard" && entry.entityType)
          .map(([name, entry]) => ({
            name,
            entityType: entry.entityType,
            appName: entry.appName,
            standardEntity: entry.standardEntity,
          }));
    },
    // XXX: THE Session list is the CONSTANT StandardContextEntities.enumerate
    // XXX: output, never the live Context registry. It used to map over
    // XXX: standardContextCandidates, which reads Context.allContexts and keeps
    // XXX: only entitySource === "standard" -- and registerStandardContexts skips
    // XXX: any name already in the registry, while the per-DC clear drops anything
    // XXX: not tagged ambient. So a page that already declared a context named
    // XXX: currentUser made "Current User" DISAPPEAR from its own picker, with no
    // XXX: error. Nothing at runtime may remove a session row: an author must
    // XXX: always be able to provide currentUser.
    sessionContextCandidates() {
      return this.availableStandardContextEntities.map(row => ({
        value: row.value,
        label: row.label,
        secondaryLabel: row.secondaryLabel ?? row.entityType,
        entityType: row.entityType,
        appName: row.appName,
        standardEntity: row.standardEntity ?? row.value,
      }));
    },
    areCurrentlyInspectedElementsInheritingOptions() {
      return false;
    },
    canValueInherit() {
      return false;
    },
    configValuesOfSelectedElements() {
      return {};
    },
    currentlyInspectedElementIdentifiers() {
      return this.currentlySelectedElementIds;
    },
    inheritedValuesOfSelectedElements() {
      return {};
    },
    inheritedValuesOfSelectedElementsByCurrentState() {
      return {};
    },
    isInheritedValueOverridden() {
      return false;
    },
    isTemplate() {
      return this.isTemplateType;
    },
    dynamicComponentSupportsVariables() {
      const type = this.dynamicComponentInstance?.type;
      return ["custom-component", "custom-page", "component"].includes(type) || this.isTemplateType;
    },
    mergedValuesOfSelectedElements() {
      return {};
    },
    optionVariables() {
      return this.dynamicComponentInstance?.variables ?? {};
    },
    rootOptions() {
      return this.dynamicComponentInstance?.rootOptions ?? {};
    },
    savingMessage() {
      return "";
    },
    errorUpdatingModule() {
      return null;
    },

    activeDropIndicator() {
      const containerIdentifier = this.dropTarget.containerIdentifier;
      const emptyIndicator = { lineAboveIdentifier: null, outlineIdentifier: null };
      if (!containerIdentifier) {
        return emptyIndicator;
      }

      const alias = containerIdentifier.includes(":") ? containerIdentifier.split(":")[1] : "";
      const containerElementId = containerIdentifier.split(":")[0];
      const container = DynamicElement.get(containerElementId);
      if (!container?.element) {
        return emptyIndicator;
      }

      const childElementIds = container.childElementIds;
      const childCount = childElementIds.length;
      const index = this.dropTarget.index;
      const identifierFor = elementId => {
        return alias ? `${elementId}:${alias}` : String(elementId);
      };

      const dragging = this.currentlyDraggingElement;
      if (dragging) {
        const draggingParentId = String(dragging.parentElement?.pk?.id ?? "");
        const draggingOwnVisualIndex = childElementIds.indexOf(String(dragging.id));
        const isSameParent = draggingParentId === String(containerElementId) && draggingOwnVisualIndex !== -1;
        if (isSameParent && (index === draggingOwnVisualIndex || index === draggingOwnVisualIndex + 1)) {
          return emptyIndicator;
        }
      }

      if (index < childCount) {
        return { lineAboveIdentifier: identifierFor(childElementIds[index]), outlineIdentifier: null };
      }
      return { lineAboveIdentifier: null, outlineIdentifier: containerIdentifier };
    },
    activeVariantsMap() {
      const destinationId = this.dynamicComponentInstance?.destinationId;
      if (!destinationId) {
        return {};
      }
      return this.activeVariantsMapForDestination(destinationId);
    },
    activeVariantsMapForDestination() {
      return destinationId => {
        if (!destinationId) {
          return {};
        }
        const studio = App.get("CreatorStudio");
        const configFeed = studio.getFeedStore("Destination", { id: destinationId }, "destinationConfiguration");
        configFeed?.triggerInitialLoad?.();
        const configItems = studio.getFeedItems("Destination", { id: destinationId }, "destinationConfiguration") ?? [];
        if (configItems.length === 0) {
          return {};
        }
        const configPk = configItems[0].entityPk;
        const variantFeed = studio.getFeedStore("DestinationConfiguration", { id: configPk.id }, "activeVariants");
        variantFeed?.triggerInitialLoad?.();
        const variantItems = studio.getFeedItems("DestinationConfiguration", { id: configPk.id }, "activeVariants") ?? [];
        const map = {};
        for (const item of variantItems) {
          const row = studio.getEntity("DestinationActiveVariant", { id: item.entityPk.id });
          const groupId = row?.variantGroupId;
          const dcId = row?.dynamicComponent?.pk?.id;
          if (groupId && dcId !== undefined && dcId !== null) {
            map[String(groupId)] = dcId;
          }
        }
        return map;
      };
    },
    destinationConfigurationEntity() {
      return this.destinationConfigurationFor(this.dynamicComponentInstance?.destinationId);
    },
    destinationConfigurationFor() {
      return destinationId => {
        if (!destinationId) {
          return null;
        }
        const studio = App.get("CreatorStudio");
        const feed = studio.getFeedStore("Destination", { id: destinationId }, "destinationConfiguration");
        feed?.triggerInitialLoad?.();
        const items = studio.getFeedItems("Destination", { id: destinationId }, "destinationConfiguration") ?? [];
        if (items.length === 0) {
          return null;
        }
        return studio.getEntity("DestinationConfiguration", { id: items[0].entityPk.id });
      };
    },
    variantsForGroup() {
      return (variantGroupId, destinationId) => {
        return variantGroupMembers("CreatorStudio", destinationId, variantGroupId);
      };
    },
    currentVariantOptions() {
      const dc = this.dynamicComponentInstance;
      if (!dc) {
        return [];
      }
      const groupId = dc.variantGroupId;
      if (!groupId) {
        return [];
      }
      return this.variantsForGroup(groupId, dc.destinationId).map(entity => ({
        id: entity.id,
        name: entity.name ?? "",
      }));
    },
    resolveActiveDynamicComponentId() {
      return referencedDcId => {
        const idNumber = Number(referencedDcId);
        if (!Number.isFinite(idNumber) || idNumber <= 0) {
          return referencedDcId;
        }
        const resolved = Number(this.canonicalVariantIdForComponent(idNumber));
        const studio = App.get("CreatorStudio");
        const target = studio.getEntity("DynamicComponent", { id: resolved });
        if (target?.status && target.status !== "active") {
          return null;
        }
        return resolved;
      };
    },
    isCanonicalVariantForGroup() {
      return dynamicComponentId => {
        const idNumber = Number(dynamicComponentId);
        if (!Number.isFinite(idNumber) || idNumber <= 0) {
          return true;
        }
        return this.canonicalVariantIdForComponent(idNumber) === idNumber;
      };
    },

    canonicalVariantIdForComponent() {
      return dynamicComponentId => {
        const idNumber = Number(dynamicComponentId);
        if (!Number.isFinite(idNumber) || idNumber <= 0) {
          return idNumber;
        }
        const studio = App.get("CreatorStudio");
        const dc = studio.getEntity("DynamicComponent", { id: idNumber });
        const groupId = dc?.variantGroupId;
        if (!groupId) {
          return idNumber;
        }
        const destinationId = dc?.destination?.pk?.id;
        const activeMap = this.activeVariantsMapForDestination(destinationId);
        const pointer = activeMap[groupId];
        const group = this.variantsForGroup(groupId, destinationId);
        if (pointer !== undefined && pointer !== null) {
          const pointerId = Number(pointer);
          if (group.some(member => Number(member.id) === pointerId)) {
            return pointerId;
          }
        }
        return group.length === 0 ? idNumber : Number(group[0].id);
      };
    },

    variantGroupMembersForComponents() {
      return dynamicComponentIds => {
        const studio = App.get("CreatorStudio");
        const members = new Set();
        dynamicComponentIds.forEach(dynamicComponentId => {
          const idNumber = Number(dynamicComponentId);
          if (!Number.isFinite(idNumber) || idNumber <= 0) {
            return;
          }
          members.add(idNumber);
          const component = studio.getEntity("DynamicComponent", { id: idNumber });
          const groupId = component?.variantGroupId;
          if (!groupId) {
            return;
          }
          this.variantsForGroup(groupId, component.destination?.pk?.id)
              .forEach(member => members.add(Number(member.id)));
        });
        return members;
      };
    },
  },

  actions: {
    drillIntoElements(descriptor) {
      this.elementsDrillPath = [...this.elementsDrillPath, descriptor];
    },
    elementsDrillBack() {
      this.elementsDrillPath = this.elementsDrillPath.slice(0, -1);
    },
    setElementsDrillPath(path) {
      this.elementsDrillPath = Array.isArray(path) ? [...path] : [];
    },
    setBreakpointPreviewWidth(alias, width) {
      this.breakpointPreviewWidths = { ...this.breakpointPreviewWidths, [alias]: width };
    },
    clearBreakpointPreviewWidth(alias) {
      const next = { ...this.breakpointPreviewWidths };
      delete next[alias];
      this.breakpointPreviewWidths = next;
    },
    clearBreakpointPreviewWidths() {
      this.breakpointPreviewWidths = {};
    },
    initialiseBuilderForDynamicComponent(
      dynamicComponentId, themeId, themeMode, availableBreakpoints, events, pages, components, entities, variants, baseUrl, hasPermissionToEdit,
      currentlySelectedElementIds = [], canvasOffsetX = 0, canvasOffsetY = 0, zoom = 1, isInspectorPaletteOpen = null,
    ) {
      if (this.dynamicComponentId && this.dynamicComponentId !== dynamicComponentId) {
        DynamicComponent.get(this.dynamicComponentId)?.flushRootOptionsSaveNow?.();
      }
      this.dynamicComponentId = dynamicComponentId;
      useCanvasVisibilityPreviewStore().setDynamicComponent(dynamicComponentId);
      this.brokenReferencesByElementId = {};
      this.loadBrokenReferences(dynamicComponentId, this.publicationIdForRequests);
      this.clearBreakpointPreviewWidths();
      this._lastKnownCurrentVersionId = null;
      this.wasRepointedExternally = false;
      this.registerUndoCaptureSink();
      void availableBreakpoints;
      this.events = events;
      this.pages = pages;
      this.components = components;
      this.entities = entities;
      this.variants = variants;
      this.baseUrl = baseUrl;
      this.hasPermissionToEdit = hasPermissionToEdit;
      this.currentlySelectedElementIds = currentlySelectedElementIds;
      this.canvasOffsetX = canvasOffsetX;
      this.canvasOffsetY = canvasOffsetY;
      this.zoom = zoom;
      this.isInspectorPaletteOpen = isInspectorPaletteOpen ?? this.isInspectorPaletteOpen;

      this.dynamicComponentInstance?.setup(themeId, themeMode, this.availableBreakpoints, entities);
      this.rootElementIdentifier = this.dynamicComponentInstance?.rootElementId;
      this.recomputeFocusSets();
      if (!this._focusSyncInstalled) {
        this._focusSyncInstalled = true;
        watch(
          () => [this.currentlySelectedElementIds, this.currentlyHoveringOverElementId, this.draggingCanvasElementBareId, this.activeBreakpointAlias],
          () => this.recomputeFocusSets(),
          { deep: true, flush: "sync" },
        );
        watch(
          () => this.currentlySelectedElementIds,
          () => writeSelectionUrlState(this.shareableSelectionBareIds),
          { deep: true },
        );
        watch(
          () => this.activeBreakpointAlias,
          alias => writeBreakpointUrlState(alias),
        );
      }
      ContextRegistryScope.staleContextNames(Context.rawContexts)
          .forEach(name => Context.removeContext(name));
    },

    setContextsEntities(entities) {
      this.entities = entities;
      this.dynamicComponentInstance?.updateContextsEntities(entities);
    },

    setBreakpointsProvider({ getAvailableBreakpoints, getHiddenBreakpointAliases, onChanged }) {
      this._breakpointsProvider = {
        getAvailableBreakpoints: typeof getAvailableBreakpoints === "function"
          ? getAvailableBreakpoints
          : () => [],
        getHiddenBreakpointAliases: typeof getHiddenBreakpointAliases === "function"
          ? getHiddenBreakpointAliases
          : () => [],
        onBreakpointsChanged: typeof onChanged === "function" ? onChanged : null,
      };
    },

    triggerEvent(eventName, payload) {
      const app = App.get("CreatorStudio");
      if (UNDOABLE_EVENTS.has(eventName) && !this._suppressUndoCapture) {
        if (this._undoSelectionBefore === null) {
          this._undoSelectionBefore = [...this.currentlySelectedElementIds];
        }
        return app.triggerUndoableEvent(eventName, payload);
      }
      return app.triggerEvent(eventName, payload);
    },

    onUndoCaptured(records) {
      const dcId = this.dynamicComponentId;
      if (!dcId) {
        return;
      }
      const selectionBefore = this._undoSelectionBefore ?? [...this.currentlySelectedElementIds];
      this._undoSelectionBefore = null;
      useBuilderUndoStore().pushUnit(dcId, {
        records,
        selectionBefore,
        selectionAfter: [...this.currentlySelectedElementIds],
      });
      this.scheduleVersionSnapshot();
    },

    registerUndoCaptureSink() {
      if (this._undoSinkRegistered) {
        return;
      }
      this._undoSinkRegistered = true;
      App.get("CreatorStudio").setUndoCaptureSink(records => this.onUndoCaptured(records));
      watch(() => this.liveCurrentVersionId, id => this.handleExternalVersionRepoint(id), { immediate: true });
      if (typeof window !== "undefined") {
        window.addEventListener(TEMP_PK_RESOLVED_EVENT, event => {
          const detail = event?.detail ?? {};
          if (detail.appName === "CreatorStudio" && detail.entityType && detail.tempPkString && detail.realPk) {
            useBuilderUndoStore().reconcileTempPk(detail.entityType, detail.tempPkString, detail.realPk);
            if (detail.entityType === "DynamicComponent") {
              this.reconcileComponentRename(detail.tempPkString, detail.realPk);
            }
          }
        });
      }
    },

    commitComponentRename(pk, name) {
      const app = App.get("CreatorStudio");
      const entity = app.getEntity("DynamicComponent", pk);
      const resolvedPk = entity.getPk();
      app.triggerEventWithoutSendingToServer("UpdateEntityField", {
        entityType: "DynamicComponent",
        entityPk: resolvedPk,
        fields: { name },
      });
      if (entity.isEntityPkTemp()) {
        this.queuedComponentRenames[App.getPkString(resolvedPk)] = name;
        return;
      }
      entity.triggerAction("updateName", {});
    },

    reconcileComponentRename(tempPkString, realPk) {
      if (this.pendingComponentRenamePkString === tempPkString) {
        this.pendingComponentRenamePkString = App.getPkString(realPk);
      }
      const queuedName = this.queuedComponentRenames[tempPkString];
      if (!queuedName) {
        return;
      }
      delete this.queuedComponentRenames[tempPkString];
      const app = App.get("CreatorStudio");
      const entity = app.getEntity("DynamicComponent", realPk);
      app.triggerEventWithoutSendingToServer("UpdateEntityField", {
        entityType: "DynamicComponent",
        entityPk: entity.getPk(),
        fields: { name: queuedName },
      });
      entity.triggerAction("updateName", {});
    },

    handleExternalVersionRepoint(newVersionId) {
      if (newVersionId === null || newVersionId >= TEMP_PK_FLOOR) {
        return;
      }
      const previous = this._lastKnownCurrentVersionId;
      this._lastKnownCurrentVersionId = newVersionId;
      if (previous !== null && newVersionId < previous) {
        this.wasRepointedExternally = true;
      }
    },

    dismissRepointNotice() {
      this.wasRepointedExternally = false;
    },

    recordOptionEditUndoUnit(pk, beforeFields, afterFields) {
      const dcId = this.dynamicComponentId;
      if (!dcId) {
        return;
      }
      const record = { entityType: "Element", pk, op: "updated", beforeFields, afterFields };
      if (this._bulkOptionEdit) {
        this._bulkOptionEdit.records.push(record);
        this._armBulkOptionEditFlush();
        return;
      }
      useBuilderUndoStore().pushUnit(dcId, {
        records: [record],
        selectionBefore: [...this.currentlySelectedElementIds],
        selectionAfter: [...this.currentlySelectedElementIds],
      });
      this.scheduleVersionSnapshot();
    },

    beginBulkOptionEdit() {
      if (this._bulkOptionEdit) {
        this._flushBulkOptionEdit();
      }
      this._bulkOptionEdit = {
        records: [],
        selectionBefore: [...this.currentlySelectedElementIds],
        timer: null,
      };
      this._armBulkOptionEditFlush();
    },

    _armBulkOptionEditFlush() {
      const bulk = this._bulkOptionEdit;
      if (!bulk) {
        return;
      }
      if (bulk.timer !== null) {
        clearTimeout(bulk.timer);
      }
      bulk.timer = setTimeout(() => this._flushBulkOptionEdit(), BULK_OPTION_EDIT_COALESCE_MS);
    },

    _flushBulkOptionEdit() {
      const bulk = this._bulkOptionEdit;
      this._bulkOptionEdit = null;
      if (!bulk) {
        return;
      }
      if (bulk.timer !== null) {
        clearTimeout(bulk.timer);
      }
      const dcId = this.dynamicComponentId;
      if (!dcId || bulk.records.length === 0) {
        return;
      }
      useBuilderUndoStore().pushUnit(dcId, {
        records: bulk.records,
        selectionBefore: bulk.selectionBefore,
        selectionAfter: [...this.currentlySelectedElementIds],
      });
      this.scheduleVersionSnapshot();
    },

    scheduleVersionSnapshot() {
      const dcId = this.dynamicComponentId;
      if (!dcId) {
        return;
      }
      if (this._versionSnapshotTimer !== null) {
        clearTimeout(this._versionSnapshotTimer);
      }
      this._versionSnapshotTimer = setTimeout(() => {
        this._versionSnapshotTimer = null;
        this.writeVersionSnapshot(dcId);
      }, VERSION_SNAPSHOT_DEBOUNCE_MS);
    },

    writeVersionSnapshot(dcId) {
      if (this.dynamicComponentId !== dcId) {
        return;
      }
      const snapshot = this.buildVersionSnapshot();
      if (!snapshot) {
        return;
      }
      App.get("CreatorStudio").triggerEvent("SnapshotComponentVersion", {
        dynamicComponent: { id: dcId },
        configuration: JSON.stringify(snapshot),
      });
    },

    buildVersionSnapshot() {
      const dcId = this.dynamicComponentId;
      if (!dcId) {
        return null;
      }
      const app = App.get("CreatorStudio");
      const items = app.getFeedItems("DynamicComponent", { id: String(dcId) }, "elements") ?? [];
      const elements = [];
      for (const item of items) {
        const elementId = item?.entityPk?.id;
        if (elementId === undefined || elementId === null) {
          continue;
        }
        const entity = app.getEntity("Element", { id: elementId });
        if (!entity || entity.isPkTemp) {
          return null;
        }
        const fields = app.getEntityFieldData("Element", { id: elementId });
        if (!fields) {
          continue;
        }
        elements.push(JSON.parse(JSON.stringify(fields)));
      }
      if (!elements.length) {
        return null;
      }
      return {
        rootElementId: this.dynamicComponentInstance?.rootElementId ?? null,
        rootOptions: app.getEntityField("DynamicComponent", { id: String(dcId) }, "rootOptions") ?? null,
        elements,
      };
    },

    whenDestinationConfigurationLoaded(attempt = 0, destinationId = null) {
      const config = destinationId
        ? this.destinationConfigurationFor(destinationId)
        : this.destinationConfigurationEntity;
      if (config?.id) {
        return Promise.resolve(config);
      }
      if (attempt >= 50) {
        return Promise.reject(new DynamicComponentError(
          "Destination configuration is not loaded.",
          { attempts: attempt },
        ));
      }
      return new Promise(resolve => setTimeout(resolve, 100))
          .then(() => this.whenDestinationConfigurationLoaded(attempt + 1, destinationId));
    },

    setActiveVariant(variantGroupId, dynamicComponentId, destinationId = null) {
      return this.whenDestinationConfigurationLoaded(0, destinationId)
          .then(config => this.fireSetActiveVariant(config, variantGroupId, dynamicComponentId));
    },

    fireSetActiveVariant(config, variantGroupId, dynamicComponentId) {
      this.triggerEvent("SetActiveVariant", {
        destinationConfiguration: { id: config.id },
        variantGroupId,
        dynamicComponent: { id: dynamicComponentId },
      });
    },

    async createTemplateComponentFromElement(elementIdentifier) {
      this._suppressUndoCapture = true;
      try {
        return await this._createTemplateComponentFromElementImpl(elementIdentifier);
      } finally {
        this._suppressUndoCapture = false;
      }
    },

    async _createTemplateComponentFromElementImpl(elementIdentifier) {
      const sourceElementId = String(elementIdentifier).split(":")[0];
      const studio = App.get("CreatorStudio");
      const sourceRow = studio.getEntity("Element", { id: sourceElementId });
      if (!sourceRow) {
        throw new DynamicComponentError("Cannot create template: source element is not loaded.", {
          elementIdentifier,
        });
      }
      const destinationId = this.dynamicComponentInstance?.destinationId;
      if (!destinationId) {
        throw new DynamicComponentError("Cannot create template: the edited DC has no destination.", {
          elementIdentifier,
        });
      }

      const sourceElements = destinationElementsOf("CreatorStudio", this.dynamicComponentId);

      const childrenByParentId = new Map();
      let sourceSubtreeRoot = null;
      sourceElements.forEach(element => {
        if (String(element.id) === sourceElementId) {
          sourceSubtreeRoot = element;
        }
        const parentId = element.parentElement?.pk?.id;
        if (!parentId) {
          return;
        }
        const siblings = childrenByParentId.get(parentId) ?? [];
        siblings.push(element);
        childrenByParentId.set(parentId, siblings);
      });
      childrenByParentId.forEach(siblings => {
        siblings.sort((a, b) => (a.indexInParent ?? 0) - (b.indexInParent ?? 0));
      });
      if (!sourceSubtreeRoot) {
        throw new DynamicComponentError("Cannot create template: source element is not part of the edited DC.", {
          elementIdentifier,
        });
      }

      const subtreeElementTypes = new Set();
      const collectSubtreeTypes = row => {
        if (row.type) {
          subtreeElementTypes.add(row.type);
        }
        (childrenByParentId.get(row.id) ?? []).forEach(collectSubtreeTypes);
      };
      collectSubtreeTypes(sourceSubtreeRoot);
      const requiredContexts = this.collectTemplateRequiredContexts(subtreeElementTypes);

      const sourceDisplayName = DynamicElement.get(sourceElementId)?.displayName
        || sourceRow.displayName
        || sourceRow.type
        || "Element";
      const templateName = `${sourceDisplayName} Template`;
      const createResult = this.triggerEvent("CreateDynamicComponent", {
        destination: { id: destinationId },
        type: "component",
        name: templateName,
        description: "",
        slug: "",
        variantGroupId: "",
        rootElementOptions: "{}",
        requiredContexts,
      });
      const newDynamicComponentTempId = createResult?.createDynamicComponent?.id;
      const newRootElementTempId = createResult?.createRootElement?.id;
      if (!newDynamicComponentTempId || !newRootElementTempId) {
        throw new DynamicComponentError("CreateDynamicComponent did not return id pair for template.", {
          elementIdentifier,
        });
      }

      const newDynamicComponentId = this.findRealVariantId(newDynamicComponentTempId);
      const newDynamicComponentEntity = studio.getEntity("DynamicComponent", { id: newDynamicComponentId });
      const newRootElementId = newDynamicComponentEntity?.rootElement?.pk?.id ?? newRootElementTempId;

      const templateSeedParentKey = `template-source:${sourceElementId}`;
      childrenByParentId.set(templateSeedParentKey, [sourceSubtreeRoot]);
      this.cloneTemplateChildrenRecursive(
        newDynamicComponentId,
        templateSeedParentKey,
        newRootElementId,
        childrenByParentId,
      );

      return this.whenVariantIdIsReal(newDynamicComponentId);
    },

    async duplicateAppComponent(sourceDynamicComponentId, destinationId) {
      this._suppressUndoCapture = true;
      try {
        return await this._duplicateAppComponentImpl(sourceDynamicComponentId, destinationId);
      } finally {
        this._suppressUndoCapture = false;
      }
    },

    duplicateAppComponentViaBuilder(sourceDynamicComponentId, destinationId) {
      const sourceId = Number(sourceDynamicComponentId);
      const destination = Number(destinationId);
      if (!sourceId || !destination) {
        throw new DynamicComponentError("Cannot duplicate: no source component or destination.", {
          sourceDynamicComponentId,
          destinationId,
        });
      }
      window.location.assign(
        `/build/${destination}/components/${sourceId}?${DUPLICATE_APP_COMPONENT_PARAM}=1`,
      );
    },

    async resumeAppComponentDuplicateFromUrl() {
      const params = new URLSearchParams(window.location.search);
      if (params.get(DUPLICATE_APP_COMPONENT_PARAM) !== "1") {
        return;
      }
      const path = window.location.pathname.match(/\/build\/(\d+)\/components\/(\d+)/);
      if (!path) {
        return;
      }
      const destinationId = Number(path[1]);
      const sourceId = Number(path[2]);
      await this._whenSourceElementsSettled(sourceId);
      const newId = await this.duplicateAppComponent(sourceId, destinationId);
      window.location.assign(`/build/${destinationId}/components/${newId}`);
    },

    async _whenSourceElementsSettled(sourceId) {
      const studio = App.get("CreatorStudio");
      const countElements = () => Object.values(studio.getAllEntitiesOfType("Element") ?? {})
          .filter(element => Number(element?.dynamicComponent?.pk?.id) === Number(sourceId))
          .length;
      let stableFor = 0;
      let previous = -1;
      for (let waited = 0; waited < DUPLICATE_HYDRATION_TIMEOUT_MS; waited += DUPLICATE_HYDRATION_POLL_MS) {
        const current = countElements();
        stableFor = current > 0 && current === previous ? stableFor + DUPLICATE_HYDRATION_POLL_MS : 0;
        if (stableFor >= DUPLICATE_HYDRATION_SETTLE_MS) {
          return;
        }
        previous = current;
        await new Promise(resolve => setTimeout(resolve, DUPLICATE_HYDRATION_POLL_MS));
      }
    },

    async _duplicateAppComponentImpl(sourceDynamicComponentId, destinationId) {
      const sourceId = Number(sourceDynamicComponentId);
      if (!sourceId || !destinationId) {
        throw new DynamicComponentError("Cannot duplicate: no source component or destination.", {
          sourceDynamicComponentId,
          destinationId,
        });
      }
      const sourceInstance = DynamicComponent.get(sourceId, false);
      await sourceInstance.setup(window.blclient?.data?.themeId ?? 0, "", [], {});

      const childrenByParentId = this._loadedChildrenByParentId(sourceId);
      if (!childrenByParentId.size) {
        throw new DynamicComponentError(
          "Cannot duplicate: the source component's elements are not loaded, so the copy would be empty.",
          { sourceDynamicComponentId },
        );
      }

      const sourceName = sourceInstance.getRootOption("component-name")
        || sourceInstance.getRootOption("display-name")
        || "Component";
      return this._createComponentCopy({
        sourceId,
        destinationId,
        name: `${sourceName} Copy`,
        sourceRootElementId: sourceInstance.rootElementId,
        requiredContexts: sourceInstance.getRootOption("required-contexts") ?? {},
        providesContexts: sourceInstance.getRootOption("provides-contexts") ?? {},
        childrenByParentId,
      });
    },

    async duplicateComponentInPlace(sourceDynamicComponentId, destinationId, name) {
      const sourceId = Number(sourceDynamicComponentId);
      if (!sourceId || !destinationId || !name) {
        throw new DynamicComponentError("Cannot duplicate: no source component, destination or name.", {
          sourceDynamicComponentId,
          destinationId,
          name,
        });
      }
      const studio = App.get("CreatorStudio");
      const sourcePk = { id: sourceId };
      await this._drainElementFeed(sourcePk);
      await this._whenSourceElementsSettled(sourceId);
      const rootOptions = parseRootOptions(studio.getEntityField("DynamicComponent", sourcePk, "rootOptions"));
      this._suppressUndoCapture = true;
      try {
        return await this._createComponentCopy({
          sourceId,
          destinationId,
          name,
          sourceRootElementId: studio.getEntity("DynamicComponent", sourcePk)?.rootElement?.pk?.id,
          requiredContexts: rootOptions["required-contexts"] ?? {},
          providesContexts: rootOptions["provides-contexts"] ?? {},
          childrenByParentId: this._loadedChildrenByParentId(sourceId),
        });
      } finally {
        this._suppressUndoCapture = false;
      }
    },

    async _drainElementFeed(dynamicComponentPk) {
      const app = App.get("CreatorStudio");
      app.triggerFeedInitialLoad("DynamicComponent", dynamicComponentPk, "elements");
      for (let waited = 0; waited < DUPLICATE_HYDRATION_TIMEOUT_MS; waited += DUPLICATE_HYDRATION_POLL_MS) {
        const loaded = app.getFeedMeta("DynamicComponent", dynamicComponentPk, "elements", "hasLoadedAtLeastOnce");
        const hasMore = app.getFeedMeta("DynamicComponent", dynamicComponentPk, "elements", "hasMoreNext");
        const loading = app.getFeedMeta("DynamicComponent", dynamicComponentPk, "elements", "loadingNext");
        if (loaded && !hasMore && !loading) {
          return;
        }
        if (loaded && hasMore && !loading) {
          app.loadMoreNext("DynamicComponent", dynamicComponentPk, "elements");
        }
        await new Promise(resolve => setTimeout(resolve, DUPLICATE_HYDRATION_POLL_MS));
      }
      throw new DynamicComponentError("Cannot duplicate: the source component's elements did not finish loading.", {
        dynamicComponentId: dynamicComponentPk.id,
        timeoutMs: DUPLICATE_HYDRATION_TIMEOUT_MS,
      });
    },

    _loadedChildrenByParentId(sourceId) {
      const childrenByParentId = new Map();
      Object.values(App.get("CreatorStudio").getAllEntitiesOfType("Element") ?? {})
          .filter(element => Number(element?.dynamicComponent?.pk?.id) === sourceId)
          .forEach(element => {
            const parentId = element.parentElement?.pk?.id;
            if (!parentId) {
              return;
            }
            const siblings = childrenByParentId.get(parentId) ?? [];
            siblings.push(element);
            childrenByParentId.set(parentId, siblings);
          });
      childrenByParentId.forEach(siblings => {
        siblings.sort((a, b) => (a.indexInParent ?? 0) - (b.indexInParent ?? 0));
      });
      return childrenByParentId;
    },

    _createComponentCopy({
      sourceId, destinationId, name, sourceRootElementId, requiredContexts, providesContexts, childrenByParentId,
    }) {
      if (!sourceRootElementId) {
        throw new DynamicComponentError("Cannot duplicate: the source component has no root element.", {
          sourceDynamicComponentId: sourceId,
        });
      }
      const studio = App.get("CreatorStudio");
      const createResult = this.triggerEvent("CreateDynamicComponent", {
        destination: { id: destinationId },
        type: "component",
        name,
        description: "",
        slug: "",
        variantGroupId: "",
        rootElementOptions: studio.getEntity("Element", { id: sourceRootElementId })?.options ?? "{}",
        requiredContexts,
        providesContexts,
      });
      const newDynamicComponentTempId = createResult?.createDynamicComponent?.id;
      const newRootElementTempId = createResult?.createRootElement?.id;
      if (!newDynamicComponentTempId || !newRootElementTempId) {
        throw new DynamicComponentError("CreateDynamicComponent did not return an id pair for the copy.", {
          sourceDynamicComponentId: sourceId,
        });
      }

      const newDynamicComponentId = this.findRealVariantId(newDynamicComponentTempId);
      const newEntity = studio.getEntity("DynamicComponent", { id: newDynamicComponentId });
      const newRootElementId = newEntity?.rootElement?.pk?.id ?? newRootElementTempId;

      this.cloneTemplateChildrenRecursive(
        newDynamicComponentId,
        sourceRootElementId,
        newRootElementId,
        childrenByParentId,
      );

      return this.whenVariantIdIsReal(newDynamicComponentId);
    },

    cloneTemplateChildrenRecursive(newDynamicComponentId, sourceParentId, newParentId, childrenByParentId) {
      const children = childrenByParentId.get(sourceParentId) ?? [];
      [...children].reverse().forEach(childRow => {
        const result = this.triggerEvent("AddElement", {
          dynamicComponent: { id: newDynamicComponentId },
          parentElement: { id: newParentId },
          elementType: childRow.type ?? "",
          displayName: childRow.displayName ?? "",
          options: childRow.options ?? "{}",
        });
        const newId = result?.createElement?.id ?? result?.id ?? null;
        if (!newId) {
          return;
        }
        this.cloneTemplateChildrenRecursive(newDynamicComponentId, childRow.id, newId, childrenByParentId);
      });
    },

    collectTemplateRequiredContexts(subtreeElementTypes) {
      const usedTypes = new Set(subtreeElementTypes);
      for (const elementType of subtreeElementTypes) {
        if (String(elementType).startsWith("StoryMetadata")) {
          usedTypes.add("StoryTitle");
        }
      }

      const dc = this.dynamicComponentInstance;
      const sourceProvides = dc?.getRootOption("provides-contexts") ?? {};
      const sourceRequired = dc?.getRootOption("required-contexts") ?? {};
      const declaredContexts = { ...sourceRequired, ...sourceProvides };
      const requiredContexts = {};
      (dc?.availableContextsEntities ?? []).forEach(contextItem => {
        const sourceDefinition = declaredContexts[contextItem.name];
        if (!sourceDefinition || contextItem.hiddenFromElementsPanel) {
          return;
        }
        if (!contextItem.entity || typeof contextItem.entity.getElements !== "function") {
          return;
        }
        let elements;
        try {
          elements = contextItem.entity.getElements();
        } catch {
          return;
        }
        if (!Array.isArray(elements)) {
          return;
        }
        if (!elements.some(element => element?.name && usedTypes.has(element.name))) {
          return;
        }
        const entry = { entity: contextItem.entityType };
        if (sourceDefinition.appName) {
          entry.appName = sourceDefinition.appName;
        }
        requiredContexts[contextItem.name] = entry;
      });
      return requiredContexts;
    },

    async createDynamicComponentVariant(sourceDynamicComponentId) {
      this._suppressUndoCapture = true;
      try {
        return await this._createDynamicComponentVariantImpl(sourceDynamicComponentId);
      } finally {
        this._suppressUndoCapture = false;
      }
    },

    async _createDynamicComponentVariantImpl(sourceDynamicComponentId) {
      const studio = App.get("CreatorStudio");
      const source = studio.getEntity("DynamicComponent", { id: sourceDynamicComponentId });
      if (!source) {
        throw new DynamicComponentError("Cannot create variant: source DynamicComponent is not loaded.", {
          sourceDynamicComponentId,
        });
      }
      let variantGroupId = source.variantGroupId;
      if (!variantGroupId) {
        variantGroupId = generateUuidV4();
        this.triggerEvent("UpdateDynamicComponentVariantGroupId", {
          dynamicComponent: { id: sourceDynamicComponentId },
          variantGroupId,
        });
      }
      const destinationId = source.destination?.pk?.id;
      const sourceRootOptionsRaw = source.rootOptions ?? "{}";
      let sourceRootOptions = {};
      try {
        sourceRootOptions = JSON.parse(sourceRootOptionsRaw) ?? {};
      } catch {
        sourceRootOptions = {};
      }

      const variantName = `${source.name ?? "Untitled"} – Variant`;
      const createResult = this.triggerEvent("CreateDynamicComponent", {
        destination: { id: destinationId },
        type: source.type,
        name: variantName,
        description: source.description ?? "",
        slug: sourceRootOptions["page-slug"] ?? "",
        variantGroupId,
        rootElementOptions: "{}",
      });
      const newDynamicComponentTempId = createResult?.createDynamicComponent?.id;
      const newRootElementTempId = createResult?.createRootElement?.id;
      if (!newDynamicComponentTempId || !newRootElementTempId) {
        throw new DynamicComponentError("CreateDynamicComponent did not return id pair for variant.", {
          sourceDynamicComponentId,
        });
      }

      const newDynamicComponentId = this.findRealVariantId(newDynamicComponentTempId);
      const newDynamicComponentEntity = studio.getEntity("DynamicComponent", { id: newDynamicComponentId });
      const newRootElementId = newDynamicComponentEntity?.rootElement?.pk?.id ?? newRootElementTempId;

      const sourceRootElementId = source.rootElement?.pk?.id;
      const sourceElements = destinationElementsOf("CreatorStudio", sourceDynamicComponentId);

      const elementsById = new Map();
      const childrenByParentId = new Map();
      sourceElements.forEach(element => {
        const elementId = element.id;
        elementsById.set(elementId, element);
        const parentId = element.parentElement?.pk?.id;
        if (!parentId) {
          return;
        }
        const siblings = childrenByParentId.get(parentId) ?? [];
        siblings.push(element);
        childrenByParentId.set(parentId, siblings);
      });
      childrenByParentId.forEach(siblings => {
        siblings.sort((a, b) => (a.indexInParent ?? 0) - (b.indexInParent ?? 0));
      });

      const idRemap = new Map();
      idRemap.set(sourceRootElementId, newRootElementId);

      const sourceRootRow = elementsById.get(sourceRootElementId);
      if (sourceRootRow) {
        this.triggerEvent("UpdateElement", {
          element: { id: newRootElementId },
          options: sourceRootRow.options ?? {},
          optionsVersion: sourceRootRow.optionsVersion ?? 0,
          type: sourceRootRow.type ?? "",
          displayName: sourceRootRow.displayName ?? "",
          overriddenOptionKeys: sourceRootRow.overriddenOptionKeys ?? "[]",
        });
      }

      this.copyVariantChildrenRecursive(
        newDynamicComponentId,
        sourceRootElementId,
        newRootElementId,
        childrenByParentId,
        idRemap,
      );

      this.remapVariantRootOptions(newDynamicComponentId, sourceRootOptions, idRemap, variantName);

      return this.whenVariantIdIsReal(newDynamicComponentId)
          .then(realId => this.setActiveVariant(variantGroupId, realId, destinationId).then(() => realId));
    },

    findRealVariantId(tempId) {
      const direct = App.get("CreatorStudio").getEntity("DynamicComponent", { id: tempId });
      if (direct && !direct.isEntityPkTemp()) {
        return direct.id;
      }
      return tempId;
    },

    whenVariantIdIsReal(tempId, attempt = 0) {
      if (Number(tempId) < TEMP_PK_FLOOR) {
        return Promise.resolve(tempId);
      }
      const resolved = App.get("CreatorStudio").resolveTempPk("DynamicComponent", { id: Number(tempId) });
      if (resolved?.id !== undefined && resolved?.id !== null && Number(resolved.id) < TEMP_PK_FLOOR) {
        return Promise.resolve(Number(resolved.id));
      }
      if (attempt >= 60) {
        return Promise.resolve(tempId);
      }
      return new Promise(resolve => setTimeout(resolve, 250))
          .then(() => this.whenVariantIdIsReal(tempId, attempt + 1));
    },

    copyVariantChildrenRecursive(newDynamicComponentId, sourceParentId, newParentId, childrenByParentId, idRemap) {
      const children = childrenByParentId.get(sourceParentId) ?? [];
      const ordered = [...children];
      ordered.forEach(childRow => {
        const result = this.triggerEvent("AddElement", {
          dynamicComponent: { id: newDynamicComponentId },
          parentElement: { id: newParentId },
          elementType: childRow.type ?? "",
          displayName: childRow.displayName ?? "",
          options: childRow.options ?? "{}",
        });
        const newId = result?.createElement?.id ?? result?.id ?? null;
        if (!newId) {
          return;
        }
        idRemap.set(childRow.id, newId);
        this.copyVariantChildrenRecursive(newDynamicComponentId, childRow.id, newId, childrenByParentId, idRemap);
      });
    },

    remapVariantRootOptions(newDynamicComponentId, sourceRootOptions, idRemap, variantName) {
      if (!sourceRootOptions || typeof sourceRootOptions !== "object") {
        return;
      }
      const next = JSON.parse(JSON.stringify(sourceRootOptions));
      if (variantName) {
        next["display-name"] = variantName;
      }
      if (next["registered-events"] && typeof next["registered-events"] === "object") {
        Object.keys(next["registered-events"]).forEach(name => {
          const event = next["registered-events"][name];
          if (!event || typeof event !== "object") {
            return;
          }
          const oldSource = Number(event.source ?? 0);
          if (oldSource > 0 && idRemap.has(oldSource)) {
            next["registered-events"][name].source = String(idRemap.get(oldSource));
          }
        });
      }
      if (next.variables && typeof next.variables === "object") {
        Object.keys(next.variables).forEach(variableId => {
          const variable = next.variables[variableId];
          if (!variable || typeof variable.linked !== "object") {
            return;
          }
          const remapped = {};
          Object.keys(variable.linked).forEach(key => {
            const parts = String(key).split(":");
            if (parts.length !== 2) {
              remapped[key] = variable.linked[key];
              return;
            }
            const oldId = Number(parts[0]);
            const newId = idRemap.get(oldId) ?? oldId;
            remapped[`${newId}:${parts[1]}`] = variable.linked[key];
          });
          next.variables[variableId].linked = remapped;
        });
      }
      this.triggerEvent("UpdateDynamicComponentRootOptions", {
        dynamicComponent: { id: newDynamicComponentId },
        rootOptions: JSON.stringify(next),
      });
    },

    setActiveBreakpoint(breakpoint) {
      this.selectBreakpoint(breakpoint);
    },

    createBreakpoint(alias, valueNumber, label) {
      if (!alias) {
        return;
      }
      const current = this.availableBreakpoints;
      if (current.some(bp => bp.alias === alias)) {
        return;
      }
      const next = [...current, { label: label || alias, value: `${valueNumber}px`, alias }];
      this._breakpointsProvider.onBreakpointsChanged?.(next, this.hiddenBreakpointAliases);
    },

    editBreakpoint(alias, valueNumber, label) {
      const next = this.availableBreakpoints.map(bp => {
        if (bp.alias !== alias) {
          return bp;
        }
        return { ...bp, label: label || bp.label, value: `${valueNumber}px` };
      });
      this._breakpointsProvider.onBreakpointsChanged?.(next, this.hiddenBreakpointAliases);
    },

    removeBreakpoint(alias) {
      const next = this.availableBreakpoints.filter(bp => bp.alias !== alias);
      const nextHidden = this.hiddenBreakpointAliases.filter(a => a !== alias);
      this._breakpointsProvider.onBreakpointsChanged?.(next, nextHidden);
    },

    toggleBreakpointVisibility(alias) {
      const set = new Set(this.hiddenBreakpointAliases);
      if (set.has(alias)) {
        set.delete(alias);
      } else {
        set.add(alias);
      }
      this._breakpointsProvider.onBreakpointsChanged?.(this.availableBreakpoints, [...set]);
    },

    generateTemplateVariableLabel(optionKey) {
      const variables = this.optionVariables ?? {};
      const existingLabels = new Set(
        Object.values(variables).map(v => v?.label).filter(Boolean),
      );
      const baseLabel = kebabToTitleCase(optionKey);
      if (!existingLabels.has(baseLabel)) {
        return baseLabel;
      }
      let copyCount = 1;
      while (existingLabels.has(`${baseLabel} (${copyCount})`)) {
        copyCount++;
      }
      return `${baseLabel} (${copyCount})`;
    },

    convertOptionToVariable(optionClass) {
      if (!this.dynamicComponentInstance || !optionClass) {
        return;
      }
      const optionInstance = new optionClass();
      const variableLabel = this.generateTemplateVariableLabel(optionInstance.optionKey);
      this.dynamicComponentInstance.createVariable(variableLabel, optionInstance);
    },

    removeTemplateVariable(templateVariableKey) {
      if (!this.dynamicComponentInstance || !templateVariableKey) {
        return;
      }
      const variableId = String(templateVariableKey).split(".")[0];
      this.dynamicComponentInstance.removeVariable(variableId);
    },

    assignVariableToSelectedElements({ option, linkToKey }) {
      const dc = this.dynamicComponentInstance;
      if (!dc || !option || !linkToKey) {
        return;
      }
      const ids = Array.isArray(this.currentlySelectedElementIds)
        ? this.currentlySelectedElementIds
        : [];
      if (ids.length === 0) {
        return;
      }
      ids.forEach(rawIdentifier => {
        const elementId = String(rawIdentifier).split(":")[0];
        const element = DynamicElement.get(elementId);
        if (!element) {
          return;
        }
        const currentValue = element.getRawOptionValue(
          option.optionKey,
          this.activeBreakpointAlias,
          "default",
        )?.r;
        dc.linkVariable(linkToKey, elementId, option.optionKey, currentValue);
      });
    },

    removeLinkedVariable(option) {
      const dc = this.dynamicComponentInstance;
      if (!dc || !option) {
        return;
      }
      const ids = Array.isArray(this.currentlySelectedElementIds)
        ? this.currentlySelectedElementIds
        : [];
      ids.forEach(rawIdentifier => {
        const elementId = String(rawIdentifier).split(":")[0];
        const variables = dc.variables ?? {};
        const lookupKey = `${elementId}:${option.optionKey}`;
        Object.keys(variables).forEach(variableId => {
          if (variables[variableId]?.linked?.[lookupKey]) {
            dc.unlinkVariable(variableId, elementId, option.optionKey);
          }
        });
      });
    },

    setExpressionOnSelectedElements({ optionKey, token }) {
      const [state, bp] = String(this.activeState).split(":");
      const breakpoint = bp || "default";
      const context = {
        themeMode: this.themeMode,
        themeId: this.themeId,
        breakpoints: this.selectableBreakpoints,
      };
      (this.currentlySelectedElementIds || []).forEach(rawIdentifier => {
        const elementId = String(rawIdentifier).split(":")[0];
        const element = DynamicElement.get(elementId);
        if (!element) {
          return;
        }
        element.updateOption(optionKey, breakpoint, state || "default", token, context);
      });
    },

    clearExpressionFromSelectedElements({ optionKey }) {
      this.setExpressionOnSelectedElements({ optionKey, token: undefined });
    },

    addElements(elementConfig, parentElement, previousElement) {
      const dynamicComponent = parentElement?.dynamicComponent?.pk;
      if (!dynamicComponent) {
        throw new DynamicComponentError("Cannot add element: Parent has no Dynamic Component association.", { parentElement });
      }

      const elementType = elementConfig.type ?? elementConfig.name;
      const rawOptions = elementConfig.options ?? elementConfig.rootOptions;

      const defaultTree = DynamicElement.buildDefaultOptionsTree(elementType, {
        themeId: this.themeId,
        themeMode: this.themeMode,
        breakpoints: this.availableBreakpoints,
        theme: this.dynamicComponentInstance?.theme ?? null,
        dynamicComponent: this.dynamicComponentInstance ?? null,
      });

      let merged = defaultTree;
      if (typeof rawOptions === "string") {
        try {
          const parsed = JSON.parse(rawOptions);
          if (parsed && !Array.isArray(parsed) && typeof parsed === "object") {
            merged = mergeDefaultsWithOverrides(defaultTree, parsed);
          }
        } catch {
          merged = defaultTree;
        }
      } else if (rawOptions && !Array.isArray(rawOptions) && typeof rawOptions === "object") {
        merged = mergeDefaultsWithOverrides(defaultTree, rawOptions);
      }

      if (elementConfig.context && typeof elementConfig.context === "object") {
        merged.context = elementConfig.context;
      }
      if (!merged.context?.entityContext) {
        const declared = DynamicElement.getElementDefinition(elementType)?.context;
        if (declared?.entityContext) {
          merged.context = { ...declared };
        }
      }
      if (!merged.context?.entityContext) {
        const classBinding = DynamicElement.entityBindingClassIndex().get(elementType);
        if (classBinding) {
          merged.context = {
            appName: "Legacy",
            entityContext: classBinding.contextName,
            entityType: classBinding.entityType,
          };
        }
      }

      const addResult = this.triggerEvent("AddElement", {
        dynamicComponent,
        parentElement: { id: parentElement.id },
        elementType,
        displayName: elementConfig.displayName ?? "",
        options: merged,
        previousElement: previousElement ? { id: previousElement.id } : undefined,
      });
      this.elementTreeMutationEpoch++;

      this.dynamicComponentInstance?.mergeInAddedElementEventsToParentRootOptions(elementType);

      const newElementId = addResult?.createElement?.id ?? addResult?.id ?? null;
      this.seedCompletionCardComponents(newElementId, elementConfig);
      this.raiseDropContextFlow(
        this.requiredContextsForDrop(elementConfig, merged),
        newElementId,
        this.storyTypeForDrop(elementConfig, merged),
        elementType,
        merged,
        elementConfig,
      );
      return newElementId;
    },

    seedDroppedContents(droppedElementId, contents) {
      if (!droppedElementId || !Array.isArray(contents) || contents.length === 0) {
        return;
      }
      const parentElement = DynamicElement.get(String(droppedElementId))?.element;
      if (!parentElement) {
        return;
      }
      let previousElement;
      for (const node of contents) {
        previousElement = this.addSerializedElementSubtree(node, parentElement, previousElement) ?? previousElement;
      }
    },

    // XXX: The COMPONENT IS CHOSEN FIRST, then its required contexts are mapped.
    // XXX: Which contexts to prompt for cannot be known until the bound component
    // XXX: is known, so queueing them ahead of the pick meant prompting for
    // XXX: whatever a NAME lookup happened to resolve. enqueueChosenComponentContexts
    // XXX: queues them from the component the author actually picked.
    raiseDropContextFlow(
      requiredContexts, droppedElementId, storyType = null, elementType = "", mergedOptions = null, elementDef = null,
    ) {
      const dropSelectionClass = BaseDropSelection.forDrop(elementType, elementDef);
      if (dropSelectionClass && dropSelectionClass.needsSelectionOnDrop(mergedOptions)) {
        this.openDropSelection({ droppedElementId, elementType, elementDef });
        return;
      }
      // XXX: PICKER FIRST (#89). The completion elements were never migrated to
      // XXX: that ordering and still queued contexts ahead of the picker -- the
      // XXX: exact inversion #89 called a logical error, because the contexts
      // XXX: worth prompting for belong to the CARDS the author has not chosen
      // XXX: yet. enqueueCompletionCardContexts queues those on resolve.
      if (COMPLETION_STATE_ELEMENT_TYPES.includes(elementType)) {
        this.openCompletionCardPicker({ droppedElementId, elementType });
        return;
      }
      this.enqueueContextPrompts(requiredContexts, droppedElementId);
      const needsCardMap = requiresStoryContext(requiredContexts) && !storyType;
      if (!needsCardMap) {
        return;
      }
      if (this.activeContextPromptBatch.length) {
        this.pendingStoryCardPickerAfterPrompts = { droppedElementId };
        return;
      }
      this.openStoryCardPicker({ trigger: "drop", droppedElementId });
    },

    openParkedStoryCardPicker() {
      const parked = this.pendingStoryCardPickerAfterPrompts;
      if (!parked || this.activeContextPromptBatch.length) {
        return;
      }
      this.pendingStoryCardPickerAfterPrompts = null;
      this.openStoryCardPicker({ trigger: "drop", droppedElementId: parked.droppedElementId });
    },

    requiredContextsForDrop(elementConfig, mergedOptions) {
      if (elementConfig.requiredContexts && Object.keys(elementConfig.requiredContexts).length) {
        return elementConfig.requiredContexts;
      }
      const referencedId = referencedComponentIdFromOptions(mergedOptions);
      if (!referencedId) {
        return {};
      }
      return readRequiredContexts(DynamicComponent.get(referencedId, false));
    },

    // XXX: The publication-level completion slots SEED an element's card choice
    // XXX: at drop time and never resolve it at render. Without this every newly
    // XXX: dropped completion element would start with no bound card -- which is
    // XXX: now a loud broken state rather than a silent fallback.
    // XXX: Skips any state the author has already chosen a card for, so a
    // XXX: re-drop or a paste cannot clobber an existing choice.
    seedCompletionCardComponents(droppedElementId, elementConfig) {
      const elementType = elementConfig.type ?? elementConfig.name;
      if (!droppedElementId || !COMPLETION_ELEMENT_TYPES.includes(elementType)) {
        return;
      }
      const element = DynamicElement.get(String(droppedElementId));
      if (!element) {
        return;
      }
      const publicationId = useConfigurationStore().publicationId;
      const assignmentStore = useCompletionAssignmentStore();
      const optionContext = {
        themeMode: this.themeMode,
        themeId: this.themeId,
        breakpoints: this.selectableBreakpoints,
      };
      const slotPrefix = elementType === "CompletionToggle" ? "toggle" : "status";

      Object.entries(COMPLETION_CARD_OPTION_BY_STATUS).forEach(([status, optionClass]) => {
        const existing = Number(element.getOptionValue?.(optionClass)) || 0;
        if (existing > 0) {
          return;
        }
        const seeded = Number(assignmentStore.resolveComponentId(publicationId, `${slotPrefix}:${status}`)) || 0;
        if (!seeded) {
          return;
        }
        element.updateOption(optionClass.optionKey, "default", "default", seeded, optionContext);
      });
    },

    storyTypeForDrop(elementConfig, mergedOptions) {
      if (elementConfig.storyType) {
        return elementConfig.storyType;
      }
      const elementType = elementConfig.type ?? elementConfig.name;
      if (elementType !== "DynamicComponentElement") {
        return null;
      }
      const referencedId = referencedComponentIdFromOptions(mergedOptions);
      if (referencedId === 0) {
        return null;
      }
      return DynamicComponent.get(referencedId, false)?.getRootOption("story-type") || null;
    },

    enqueueContextPrompts(
      requiredContexts, sourceElementId = null, { isRebind = false, completionCardState = null } = {},
    ) {
      const rows = this.requiredContextPromptRows(requiredContexts);
      if (!rows.length) {
        return;
      }
      const queuedKeys = new Set(this.pendingContextPrompts.map(prompt => completionPromptKey(prompt)));
      const fresh = rows
          .map(entry => ({ ...entry, sourceElementId, isRebind, completionCardState }))
          .filter(entry => !queuedKeys.has(completionPromptKey(entry)));
      if (fresh.length) {
        this.pendingContextPrompts = [...this.pendingContextPrompts, ...fresh];
      }
    },

    repointBoundComponent(elementIdentifier, newComponentId) {
      const element = DynamicElement.get(String(elementIdentifier));
      if (!element) {
        return;
      }
      const nextId = Number(newComponentId) || 0;
      const currentId = Number(element.getRawOptionValue(
        REFERENCED_COMPONENT_ID_KEY, "default", "default",
      )?.r) || 0;
      const optionContext = {
        themeMode: this.themeMode,
        themeId: this.themeId,
        breakpoints: this.selectableBreakpoints,
      };
      element.updateOption(REFERENCED_COMPONENT_ID_KEY, "default", "default", nextId, optionContext);
      if (nextId === currentId) {
        return;
      }

      const required = nextId ? readRequiredContexts(DynamicComponent.get(nextId, false)) : {};
      const bindings = element.getRawOptionValue("context-bindings", "default", "default")?.r ?? {};
      const { kept, discarded } = partitionContextBindings(bindings, Object.keys(required));

      const discardedNames = Object.keys(discarded);
      if (discardedNames.length) {
        element.updateOption("context-bindings", "default", "default", kept, optionContext);
        const message = `Dropped ${discardedNames.length} context binding(s) that component `
          + `${nextId} does not require: ${discardedNames.join(", ")}.`;
        console.warn(`[DCE] ${message}`, { elementIdentifier, from: currentId, to: nextId, discarded }); // eslint-disable-line no-console
        new ToastBuilder()
            .setTitle("Context bindings dropped")
            .setText(message)
            .setToastType(ToastBuilder.TOAST_TYPE_STANDARD)
            .setTimeout(8000)
            .setClosable(true)
            .build();
      }

      const unsatisfied = this.unsatisfiedRequiredContexts(required, kept);
      if (unsatisfied.length) {
        this.enqueueContextPrompts(
          Object.fromEntries(unsatisfied.map(row => [row.name, required[row.name]])),
          String(elementIdentifier).split(":")[0],
          { isRebind: true },
        );
      }
    },

    beginRequiredContextRebind(elementIdentifier, row) {
      const sourceElementId = String(elementIdentifier).split(":")[0];
      const element = DynamicElement.get(String(elementIdentifier));
      const bindings = element?.getRawOptionValue("context-bindings", "default", "default")?.r ?? {};
      const rawBinding = bindings?.[row.name];
      let initialData;
      if (RequiredContextValue.isEntityBinding(rawBinding) || RequiredContextValue.isPrimitiveBinding(rawBinding)) {
        initialData = rawBinding;
      } else {
        const sourceName = rawBinding || row.name;
        initialData = this.dynamicComponentInstance?.getRootOption("provides-contexts")?.[sourceName] ?? null;
      }
      const prompt = {
        name: row.name,
        entityType: row.entityType,
        valueType: row.valueType ?? null,
        appName: row.appName ?? null,
        sourceElementId,
        isRebind: true,
        initialData,
      };
      this.pendingContextPrompts = [
        ...this.pendingContextPrompts.filter(existing => existing.name !== row.name),
        prompt,
      ];
    },

    resolveContextPromptBatch(entries) {
      const valid = (entries ?? []).filter(entry => entry?.name && entry.data);
      if (!valid.length) {
        return;
      }
      const promptsByKey = new Map(this.pendingContextPrompts.map(prompt => [completionPromptKey(prompt), prompt]));
      const resolvedKeys = new Set();
      valid.forEach(entry => {
        const key = entry.key ?? entry.name;
        const prompt = promptsByKey.get(key);
        if (!prompt) {
          throw new Error(
            `resolveContextPromptBatch: no pending prompt for "${key}" — the modal `
            + "returned a context the queue never asked for, so there is no element to bind it to.",
          );
        }
        resolvedKeys.add(key);
        if (prompt.completionCardState) {
          this.stageCompletionPromptBinding(prompt, bindingValueFor(entry));
          return;
        }
        this.applyInstanceContextBinding(prompt.sourceElementId, entry.name, bindingValueFor(entry));
      });
      this.pendingContextPrompts = this.pendingContextPrompts
          .filter(prompt => !resolvedKeys.has(completionPromptKey(prompt)));
      this.openParkedStoryCardPicker();
    },

    stageCompletionPromptBinding(prompt, binding) {
      const picker = this.activeCompletionCardPrompt;
      if (!picker) {
        throw new DynamicComponentError(
          `stageCompletionPromptBinding: the "${prompt.name}" context for the ${prompt.completionCardState} `
          + "completion card was answered, but the Select Completion Cards picker that asked for it is closed, "
          + "so the binding has nowhere to be staged and would be lost.",
          { sourceElementId: prompt.sourceElementId, state: prompt.completionCardState, name: prompt.name },
        );
      }
      picker.contextBindings = stageCompletionContextBinding(
        picker.contextBindings, prompt.completionCardState, prompt.name, binding,
      );
      if (picker.problems?.[prompt.completionCardState]) {
        picker.problems = { ...picker.problems, [prompt.completionCardState]: [] };
      }
    },

    applyInstanceContextBinding(sourceElementId, requiredName, binding) {
      const element = resolveDropSourceElement(sourceElementId);
      if (!element) {
        throw new Error(
          `applyInstanceContextBinding: element ${sourceElementId} does not resolve, so the `
          + `binding for required context "${requiredName}" has nowhere to live. The author `
          + "picked a source and pressed Save; silently dropping it would read as a broken context.",
        );
      }
      const raw = element.getRawOptionValue("context-bindings", "default", "default")?.r;
      const current = raw && typeof raw === "object" ? raw : {};
      const merged = { ...current, [requiredName]: binding };
      element.updateOption("context-bindings", "default", "default", merged, {
        themeMode: this.themeMode,
        themeId: this.themeId,
        breakpoints: this.selectableBreakpoints,
      });
    },

    dismissContextPrompts() {
      const sourceElementIds = [...new Set(
        this.pendingContextPrompts
            .filter(prompt => !prompt.isRebind)
            .map(prompt => prompt.sourceElementId)
            .filter(id => id !== null && id !== undefined),
      )];
      // XXX: A prompt raised BY a completion-card pick belongs to that row.
      // XXX: Cancelling it un-picks the row and leaves the picker open, rather
      // XXX: than stranding a card whose contexts were never mapped.
      const unpicked = this.pendingContextPrompts
          .filter(prompt => !prompt.isCompletionRebind)
          .map(prompt => prompt.completionCardState)
          .filter(state => state !== null && state !== undefined);
      if (unpicked.length && this.activeCompletionCardPrompt) {
        const picks = { ...this.activeCompletionCardPrompt.picks };
        unpicked.forEach(state => delete picks[state]);
        this.activeCompletionCardPrompt.picks = picks;
      }
      this.pendingContextPrompts = [];
      this.pendingStoryCardPickerAfterPrompts = null;
      sourceElementIds.forEach(elementId => this.deleteDismissedDropElement(elementId));
    },

    enqueueFeedComponentPrompt(payload) {
      if (!payload) {
        this.activeFeedComponentPrompt = null;
        return;
      }
      this.activeFeedComponentPrompt = {
        ...payload,
        pickBeforePrompt: this.feedComponentsMapFor(payload.feedElementId),
      };
    },

    resolveFeedComponentPromptBatch(entries) {
      const prompt = this.activeFeedComponentPrompt;
      if (!prompt) {
        return;
      }
      if (!DynamicElement.get(String(prompt.feedElementId))) {
        this.activeFeedComponentPrompt = null;
        return;
      }
      const bindings = {};
      (entries ?? []).forEach(entry => {
        if (entry?.name && entry.data) {
          bindings[entry.name] = entry.data;
        }
      });
      const leaf = {
        componentId: Number(prompt.materializedComponentId || prompt.componentId || 0),
        requiredContextSourcing: FeedComponentsMap.sourcingFromBindings(bindings, {
          rowContextName: prompt.contextVarName,
        }),
      };
      this.applyFeedComponentsMap(prompt.feedElementId, {
        ...this.feedComponentsMapFor(prompt.feedElementId),
        [FeedComponentsMap.DEFAULT_KEY]: leaf,
      });
      this.activeFeedComponentPrompt = null;
      if (prompt.openStoryCardPickerOnResolve) {
        this.openStoryCardPicker({
          trigger: "feed",
          publicationId: useConfigurationStore().publicationId,
          feedElementId: prompt.feedElementId,
          pickBeforePrompt: prompt.pickBeforePrompt,
        });
      }
    },

    feedComponentsMapFor(feedElementId) {
      if (!feedElementId) {
        return {};
      }
      const element = DynamicElement.get(String(feedElementId));
      const leaf = element?.getRawOptionValue(
        FeedComponentsMap.OPTION_KEY, this.activeBreakpointAlias, this.optionState,
      );
      const map = leaf?.r;
      return map && typeof map === "object" ? structuredClone(toRaw(map)) : {};
    },

    applyFeedComponentsMap(feedElementId, map) {
      const element = DynamicElement.get(String(feedElementId));
      if (!element) {
        return;
      }
      element.retireOptionKey(FeedComponentsMap.LEGACY_OPTION_KEY);
      element.updateOption(
        FeedComponentsMap.OPTION_KEY,
        this.activeBreakpointAlias,
        this.optionState,
        map,
        {
          themeMode: this.themeMode,
          themeId: this.themeId,
          breakpoints: this.selectableBreakpoints,
        },
      );
    },

    dismissFeedComponentPrompt() {
      const prompt = this.activeFeedComponentPrompt;
      this.activeFeedComponentPrompt = null;
      if (!prompt?.feedElementId) {
        return;
      }
      this.restoreFeedPick(prompt.feedElementId, prompt.pickBeforePrompt);
    },

    restoreFeedPick(feedElementId, pick) {
      const element = DynamicElement.get(String(feedElementId));
      if (!element) {
        return;
      }
      element.updateOption(
        FeedComponentsMap.OPTION_KEY,
        this.activeBreakpointAlias,
        this.optionState,
        pick,
        {
          themeMode: this.themeMode,
          themeId: this.themeId,
          breakpoints: this.selectableBreakpoints,
        },
      );
    },

    openStoryCardPicker(payload = {}) {
      const feedElementId = payload.feedElementId ?? null;
      let pickBeforePrompt = payload.pickBeforePrompt;
      if (pickBeforePrompt === undefined && feedElementId) {
        pickBeforePrompt = this.feedComponentsMapFor(feedElementId);
      }
      this.activeStoryCardPrompt = {
        trigger: payload.trigger ?? "feed",
        droppedElementId: payload.droppedElementId ?? null,
        publicationId: payload.publicationId
          ?? useConfigurationStore?.()?.publicationId
          ?? 0,
        storyType: payload.storyType ?? "",
        feedElementId,
        pickBeforePrompt,
      };
    },

    openDropSelection({ droppedElementId, elementType, elementDef = null, trigger = "drop" }) {
      this.activeDropSelection = { droppedElementId, elementType, elementDef, trigger };
    },

    openDropSelectionForEdit(elementId) {
      const element = DynamicElement.get(String(elementId));
      const elementType = element?.element?.type ?? "";
      const elementDef = element?.elementDef ?? null;
      if (!elementType || !BaseDropSelection.forDrop(elementType, elementDef)) {
        return;
      }
      this.openDropSelection({ droppedElementId: elementId, elementType, elementDef, trigger: "edit" });
    },

    dropSelectionValuesFor(elementId, elementType) {
      const selectionClass = BaseDropSelection.forElementType(elementType);
      const element = DynamicElement.get(String(elementId));
      if (!selectionClass || !element) {
        return {};
      }
      const values = {};
      selectionClass.rows().forEach(row => {
        const leaf = element.getRawOptionValue(row.optionKey, this.activeBreakpointAlias, this.optionState);
        values[row.optionKey] = selectionClass.coerceValue(leaf?.r ?? leaf ?? 0);
      });
      return values;
    },

    resolveDropSelection(values) {
      const selection = this.activeDropSelection;
      const selectionClass = this.dropSelectionClass;
      if (!selectionClass) {
        this.cancelDropSelection();
        return;
      }
      const rows = selectionClass.rows();
      const isEdit = selection?.trigger === "edit";
      const keepsPick = pick => {
        if (!selectionClass.isEmptyValue(pick.value)) {
          return true;
        }
        return isEdit && pick.row.optional;
      };
      const picks = rows
          .map(row => ({ row, value: dropSelectionValueForRow(values, row, rows) }))
          .filter(keepsPick)
          .map(pick => ({ optionKey: pick.row.optionKey, value: pick.value }));
      const answeredEveryRequiredRow = rows
          .filter(row => !row.optional)
          .every(row => picks.some(
            pick => pick.optionKey === row.optionKey && !selectionClass.isEmptyValue(pick.value),
          ));
      if (!answeredEveryRequiredRow) {
        this.cancelDropSelection();
        return;
      }
      this.activeDropSelection = null;
      const element = selection ? resolveDropSourceElement(selection.droppedElementId) : null;
      if (!element) {
        return;
      }
      picks.forEach(pick => element.updateOption(
        pick.optionKey,
        this.activeBreakpointAlias,
        this.optionState,
        selectionClass.coerceValue(pick.value),
        {
          themeMode: this.themeMode,
          themeId: this.themeId,
          breakpoints: this.selectableBreakpoints,
        },
      ));
      if (!isEdit) {
        this.enqueueChosenComponentContexts(selection?.droppedElementId, picks);
      }
    },

    // XXX: The author has just chosen the component, so its required contexts are
    // XXX: finally knowable. Cancelling these prompts removes the element via
    // XXX: dismissContextPrompts -- which is what makes a finished drop either
    // XXX: fully valid or gone.
    enqueueChosenComponentContexts(droppedElementId, picks) {
      if (!droppedElementId) {
        return;
      }
      const componentPick = picks.find(pick => pick.optionKey === REFERENCED_COMPONENT_ID_KEY);
      const chosenId = Number(componentPick?.value) || 0;
      if (!chosenId) {
        return;
      }
      this.enqueueContextPrompts(
        readRequiredContexts(DynamicComponent.get(chosenId, false)),
        droppedElementId,
      );
    },

    cancelDropSelection() {
      const selection = this.activeDropSelection;
      this.activeDropSelection = null;
      if (selection?.trigger === "drop" && selection?.droppedElementId) {
        this.deleteDismissedDropElement(selection.droppedElementId);
      }
    },

    createDropSelectionCandidate(name) {
      const selectionClass = this.dropSelectionClass;
      const trimmedName = String(name ?? "").trim();
      if (!selectionClass?.canCreate() || !trimmedName) {
        return Promise.resolve(0);
      }
      return Promise.resolve(selectionClass.createCandidate(this, trimmedName));
    },

    createComponentForDropSelection(name) {
      const dynamicComponentId = this.dynamicComponentInstance?.id;
      if (dynamicComponentId === null || dynamicComponentId === undefined) {
        // eslint-disable-next-line no-console
        console.error("createComponentForDropSelection: no dynamic component in scope, nothing created", { name });
        return Promise.resolve(0);
      }
      const app = App.get("CreatorStudio");
      const destinationRef = app.getEntityField("DynamicComponent", { id: String(dynamicComponentId) }, "destination");
      if (!destinationRef?.pk) {
        // eslint-disable-next-line no-console
        console.error("createComponentForDropSelection: no destination on the dynamic component", { name });
        return Promise.resolve(0);
      }
      app.triggerEventWithoutSendingToServer("UpdateEntityField", {
        entityType: "Destination",
        entityPk: destinationRef.pk,
        fields: { newPageName: name },
      });
      const result = app.getEntity("Destination", destinationRef.pk).triggerAction(
        "createDynamicComponentComponent",
        { currentDestination: { entityType: "Destination", pk: destinationRef.pk } },
      );
      const temporaryId = result?.createDynamicComponent?.id;
      if (temporaryId === undefined || temporaryId === null) {
        // eslint-disable-next-line no-console
        console.error("createComponentForDropSelection: createDynamicComponentComponent returned no id", { name });
        return Promise.resolve(0);
      }
      return this.awaitRealDynamicComponentId(temporaryId);
    },

    awaitRealDynamicComponentId(temporaryId, attempt = 0) {
      const identifier = Number(temporaryId);
      if (identifier < TEMP_PK_MINIMUM) {
        return Promise.resolve(identifier);
      }
      const resolvedPk = App.get("CreatorStudio").resolveTempPk("DynamicComponent", { id: identifier });
      const resolvedId = Number(resolvedPk?.id);
      if (Number.isFinite(resolvedId) && resolvedId > 0 && resolvedId < TEMP_PK_MINIMUM) {
        return Promise.resolve(resolvedId);
      }
      if (attempt >= CREATED_COMPONENT_PK_ATTEMPTS) {
        // eslint-disable-next-line no-console
        console.error(
          "awaitRealDynamicComponentId: created component never reconciled to a real pk; "
          + "nothing was linked so the drop can be cancelled safely",
          { temporaryId },
        );
        return Promise.resolve(0);
      }
      return new Promise(resolve => {
        setTimeout(
          () => resolve(this.awaitRealDynamicComponentId(identifier, attempt + 1)),
          CREATED_COMPONENT_PK_POLL_MS,
        );
      });
    },

    // XXX: elementType is REQUIRED. CompletionStatus and CompletionToggle are
    // XXX: otherwise indistinguishable -- identical option keys, identical
    // XXX: option classes -- so elementType is the ONLY thing that can pick the
    // XXX: right slot family. A missing one used to fall through a
    // XXX: `=== "CompletionToggle" ? "toggle" : "status"` ternary and silently
    // XXX: offer a toggle author the STATUS cards.
    openCompletionCardPicker({ droppedElementId, elementType, trigger = "drop" }) {
      if (!COMPLETION_STATE_ELEMENT_TYPES.includes(elementType)) {
        throw new DynamicComponentError(
          "openCompletionCardPicker: elementType must be one of "
          + `${COMPLETION_STATE_ELEMENT_TYPES.join(", ")} -- got ${JSON.stringify(elementType)}. `
          + "Without it the picker cannot tell a Completion Toggle from a Completion Status "
          + "and would offer the wrong slot family.",
        );
      }
      const boundBefore = this.completionBindingsFor(droppedElementId);
      const contextBindingsBefore = this.completionContextBindingsFor(droppedElementId);
      this.activeCompletionCardPrompt = {
        droppedElementId,
        elementType,
        trigger,
        // XXX: An EDIT opens showing what is bound NOW -- an author changing a
        // XXX: binding has to see what they are changing, and empty rows read as
        // XXX: "nothing is bound", which is a lie.
        // XXX: A DROP opens EMPTY (#109): selecting is what raises the required-
        // XXX: context prompt, so a pre-filled row would skip it. The drop path
        // XXX: can arrive pre-seeded from the publication slot assignments, hence
        // XXX: the branch rather than seeding unconditionally.
        picks: trigger === "edit" ? { ...boundBefore } : {},
        boundBefore,
        contextBindings: trigger === "edit" ? JSON.parse(JSON.stringify(contextBindingsBefore)) : {},
        contextBindingsBefore,
        problems: {},
      };
    },

    completionContextBindingsFor(elementId) {
      const element = resolveDropSourceElement(elementId);
      const raw = element?.getRawOptionValue(COMPLETION_CARD_CONTEXT_BINDINGS_OPTION_KEY, "default", "default")?.r;
      return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
    },

    // XXX: The before-snapshot that makes an EDIT cancel a no-op. A DROP cancel
    // XXX: still removes the element (#89); an EDIT cancel must put the element
    // XXX: back exactly as it was -- including leaving a legacy-broken element
    // XXX: broken and on the canvas, because the author may have opened the
    // XXX: picker only to go and look up which component they actually want.
    completionBindingsFor(elementId) {
      const element = resolveDropSourceElement(elementId);
      if (!element) {
        return {};
      }
      const bindings = {};
      Object.entries(COMPLETION_CARD_OPTION_BY_STATUS).forEach(([status, optionClass]) => {
        // XXX: getOptionValue returns the CRI LEAF ({r: raw, c: compiled}), not a
        // XXX: scalar. Number({r:9948}) is NaN, which `|| 0` then turned into 0 --
        // XXX: so this snapshot was ALWAYS all-zeros and both the things that
        // XXX: depend on it (the edit-cancel revert, and seeding the picker with
        // XXX: what is currently bound) silently did nothing. Read `r`, the
        // XXX: authored value, the way every other consumer does.
        const leaf = element.getOptionValue(
          optionClass.optionKey,
          this.activeBreakpointAlias,
          this.optionState,
          { themeMode: this.themeMode },
        );
        const value = leaf && typeof leaf === "object" ? leaf.r ?? leaf.c : leaf;
        bindings[status] = Number(value) || 0;
      });
      return bindings;
    },

    resolveCompletionCardPrompt(selections) {
      const prompt = this.activeCompletionCardPrompt;
      const element = prompt ? resolveDropSourceElement(prompt.droppedElementId) : null;
      if (!element) {
        this.activeCompletionCardPrompt = null;
        return;
      }
      const cardIdsByState = {};
      Object.entries(selections).forEach(([completionStatus, dynamicComponentId]) => {
        if (COMPLETION_CARD_OPTION_BY_STATUS[completionStatus]) {
          cardIdsByState[completionStatus] = Number(dynamicComponentId);
        }
      });
      const bindingsByState = pickedStateBindings(cardIdsByState, prompt.contextBindings);
      const gaps = completionCardContextGaps({
        elementId: element.id,
        elementType: prompt.elementType,
        cardIdsByState,
        bindingsByState,
        cardsById: this.completionCardsById(Object.values(cardIdsByState)),
      });
      if (gaps.length) {
        prompt.problems = completionCardContextGapsByState(gaps);
        console.error( // eslint-disable-line no-console
          `[DCE] Select Completion Cards: Save refused for element ${element.id}: `
          + `${gaps.map(describeCompletionCardContextGap).join(" ")}`,
          { elementId: element.id, gaps },
        );
        return;
      }
      this.activeCompletionCardPrompt = null;
      const context = {
        themeMode: this.themeMode,
        themeId: this.themeId,
        breakpoints: this.selectableBreakpoints,
      };
      Object.entries(cardIdsByState).forEach(([completionStatus, dynamicComponentId]) => {
        element.updateOption(
          COMPLETION_CARD_OPTION_BY_STATUS[completionStatus].optionKey,
          this.activeBreakpointAlias,
          this.optionState,
          dynamicComponentId,
          context,
        );
      });
      element.updateOption(COMPLETION_CARD_CONTEXT_BINDINGS_OPTION_KEY, "default", "default", bindingsByState, context);
      element.retireOptionKey(LEGACY_FLAT_CONTEXT_BINDINGS_OPTION_KEY);
    },

    repairLegacyCompletionContextBindings(elementIdentifier) {
      const element = resolveDropSourceElement(String(elementIdentifier).split(":")[0]);
      if (!element) {
        return false;
      }
      const cardIdsByState = this.completionBindingsFor(element.id);
      const requiredByCardId = {};
      Object.values(cardIdsByState).map(Number).filter(id => id > 0).forEach(id => {
        const component = DynamicComponent.get(id, false);
        requiredByCardId[id] = component?.isHydrated ? readRequiredContexts(component) ?? {} : null;
      });
      const plan = planLegacyCompletionContextRepair({
        optionsTree: element.parsedOptions,
        cardIdsByState,
        requiredByCardId,
        bindingsByState: this.completionContextBindingsFor(element.id),
      });
      if (!plan.ready || !plan.changed) {
        return plan.ready;
      }
      const context = { themeMode: this.themeMode, themeId: this.themeId, breakpoints: this.selectableBreakpoints };
      element.updateOption(COMPLETION_CARD_CONTEXT_BINDINGS_OPTION_KEY, "default", "default", plan.bindingsByState, context);
      if (plan.retireFlatKey) {
        element.retireOptionKey(LEGACY_FLAT_CONTEXT_BINDINGS_OPTION_KEY);
      }
      console.warn( // eslint-disable-line no-console
        `[DCE] repaired the context bindings of ${element.type} ${element.id} saved before per-state bindings: `
        + `moved ${JSON.stringify(plan.moved)} to the states whose cards need them, `
        + `left unbound ${JSON.stringify(plan.unattributed)} (no bound card needs them).`,
        { elementId: element.id, bindingsByState: plan.bindingsByState },
      );
      return true;
    },

    completionCardRequiredContexts(cardId) {
      const id = Number(cardId) || 0;
      return id > 0 ? readRequiredContexts(DynamicComponent.get(id, false)) ?? {} : {};
    },

    completionElementStoryContextName(elementId) {
      return resolveDropSourceElement(elementId)?.parsedOptions?.context?.entityContext || "story";
    },

    completionCardsById(cardIds) {
      const cards = {};
      cardIds.map(Number).filter(id => id > 0).forEach(id => {
        const component = DynamicComponent.get(id, false);
        if (!component?.isHydrated) {
          return;
        }
        cards[id] = { name: component.name ?? `Component ${id}`, required: readRequiredContexts(component) ?? {} };
      });
      return cards;
    },

    // XXX: Each chosen card is its own component with its own required contexts.
    // XXX: enqueueChosenComponentContexts cannot serve this: it reads a SINGLE
    // XXX: pick keyed REFERENCED_COMPONENT_ID_KEY, which no completion option
    // XXX: uses, so it returns early and three components' contexts are never
    // XXX: prompted for. Walk all three.
    // XXX: Picking a card raises that card's required-context prompt IMMEDIATELY,
    // XXX: stacked over the still-open picker (owner ruling 2026-08-29).
    // XXX: Selection is the trigger, so the prompt has to follow the selection,
    // XXX: not a later Save -- otherwise the author answers for three cards at
    // XXX: once with no idea which context belongs to which.
    selectCompletionCard(completionStatus, dynamicComponentId) {
      const prompt = this.activeCompletionCardPrompt;
      if (!prompt) {
        return;
      }
      const chosenId = Number(dynamicComponentId) || 0;
      prompt.picks = { ...prompt.picks, [completionStatus]: chosenId };
      prompt.problems = { ...prompt.problems, [completionStatus]: [] };
      const required = chosenId > 0 ? readRequiredContexts(DynamicComponent.get(chosenId, false)) ?? {} : {};
      const { contextBindings } = pruneStateBindingsToRequired(
        prompt.contextBindings, completionStatus, Object.keys(required),
      );
      prompt.contextBindings = contextBindings;
      const unbound = unboundRequiredContexts(required, contextBindings[completionStatus]);
      if (!unbound.length) {
        return;
      }
      // XXX: completionCardState tags these prompts so a cancel can un-pick the
      // XXX: ROW that raised them, and so two states asking for the same context
      // XXX: name get a prompt each. isRebind keeps dismissContextPrompts from
      // XXX: deleting the element: the picker is still open behind the prompt.
      this.enqueueContextPrompts(
        Object.fromEntries(unbound.map(({ name }) => [name, required[name]])),
        prompt.droppedElementId,
        { isRebind: true, completionCardState: completionStatus },
      );
    },

    beginCompletionContextRebind(completionStatus, row) {
      const picker = this.activeCompletionCardPrompt;
      if (!picker) {
        throw new DynamicComponentError(
          `beginCompletionContextRebind: no Select Completion Cards picker is open to rebind "${row?.name}" in`,
          { completionStatus, row },
        );
      }
      const staged = picker.contextBindings?.[completionStatus]?.[row.name];
      let initialData = null;
      if (RequiredContextValue.isEntityBinding(staged) || RequiredContextValue.isPrimitiveBinding(staged)) {
        initialData = staged;
      } else if (typeof staged === "string" && staged) {
        initialData = this.dynamicComponentInstance?.getRootOption("provides-contexts")?.[staged]
          ?? { entitySource: "context", sourceContext: staged, entityType: row.entityType };
      }
      const prompt = {
        name: row.name,
        entityType: row.entityType,
        valueType: row.valueType ?? null,
        appName: row.appName ?? null,
        sourceElementId: picker.droppedElementId,
        isRebind: true,
        isCompletionRebind: true,
        completionCardState: completionStatus,
        initialData,
      };
      this.pendingContextPrompts = [
        ...this.pendingContextPrompts.filter(existing => completionPromptKey(existing) !== completionPromptKey(prompt)),
        prompt,
      ];
    },

    dismissCompletionCardPrompt() {
      const prompt = this.activeCompletionCardPrompt;
      this.activeCompletionCardPrompt = null;
      if (!prompt?.droppedElementId) {
        return;
      }
      if (prompt.trigger === "edit") {
        this.restoreCompletionBindings(prompt.droppedElementId, prompt.boundBefore);
        return;
      }
      this.deleteDismissedDropElement(prompt.droppedElementId);
    },

    // XXX: Nothing is written until Save -- the per-state context bindings
    // XXX: included, since they are staged in the picker -- so this is
    // XXX: belt-and-braces: it restores the snapshot taken when the picker opened.
    restoreCompletionBindings(elementId, boundBefore) {
      const element = resolveDropSourceElement(elementId);
      if (!element || !boundBefore) {
        return;
      }
      const context = {
        themeMode: this.themeMode,
        themeId: this.themeId,
        breakpoints: this.selectableBreakpoints,
      };
      Object.entries(boundBefore).forEach(([status, dynamicComponentId]) => {
        const optionClass = COMPLETION_CARD_OPTION_BY_STATUS[status];
        if (!optionClass) {
          return;
        }
        element.updateOption(
          optionClass.optionKey,
          this.activeBreakpointAlias,
          this.optionState,
          Number(dynamicComponentId) || 0,
          context,
        );
      });
    },

    closeStoryCardPicker({ cancelled = false } = {}) {
      const prompt = this.activeStoryCardPrompt;
      this.activeStoryCardPrompt = null;
      if (!cancelled || !prompt) {
        return;
      }
      if (prompt.trigger === "drop" && prompt.droppedElementId) {
        this.deleteDismissedDropElement(prompt.droppedElementId);
        return;
      }
      if (prompt.trigger === "feed" && prompt.feedElementId && prompt.pickBeforePrompt !== undefined) {
        this.restoreFeedPick(prompt.feedElementId, prompt.pickBeforePrompt);
      }
    },

    // XXX: Called by the canvas per-element error boundary. Recorded rather than
    recordElementRenderError(elementId, message) {
      const key = String(elementId).split(":")[0];
      if (this.renderErrorsByElementId[key] === message) {
        return;
      }
      this.renderErrorsByElementId = { ...this.renderErrorsByElementId, [key]: message };
    },
    deleteDismissedDropElement(elementId, attempt = 0) {
      const app = App.get("CreatorStudio");
      const isTempPk = Number(elementId) >= 1e10;
      const resolvedPk = isTempPk ? app.resolveTempPk("Element", { id: Number(elementId) }) : { id: elementId };
      if (resolvedPk?.id !== undefined && resolvedPk?.id !== null) {
        const element = DynamicElement.get(resolvedPk.id);
        if (element?.element) {
          this.deleteElements([element]);
          return;
        }
      }
      if (attempt >= 60) {
        // eslint-disable-next-line no-console
        console.error(
          "dismissContextPrompts: cancelled drop's element never reconciled to a real pk — "
          + "it could not be removed automatically; delete it from the canvas manually",
          { elementId },
        );
        return;
      }
      setTimeout(() => this.deleteDismissedDropElement(elementId, attempt + 1), 250);
    },

    rebindInspectedElementContext(contextName) {
      const elements = this.currentlyInspectedElements;
      if (elements.length !== 1) {
        return;
      }
      const name = typeof contextName === "object" && contextName !== null
        ? contextName.value
        : contextName;
      elements[0].rebindEntityContext(name);
    },

    renameProvidedContextBindings(fromName, toName) {
      const dc = this.dynamicComponentInstance;
      if (!dc || !fromName || !toName || fromName === toName) {
        return 0;
      }
      let rebound = 0;
      const stack = [dc.rootElementId].filter(id => id !== null && id !== undefined);
      while (stack.length) {
        const element = DynamicElement.get(stack.pop());
        if (!element?.element) {
          continue;
        }
        element.childElementIds.forEach(childId => stack.push(childId));
        const options = element.parsedOptions;
        const renamed = renameValueContextReferences(options, fromName, toName);
        if (options?.context?.entityContext === fromName) {
          element.rebindEntityContext(toName, renamed);
          rebound += 1;
        } else if (renamed !== options) {
          element.replaceOptions(renamed);
          rebound += 1;
        }
      }
      return rebound;
    },

    removeElementsRequiringContext(contextName) {
      const dc = this.dynamicComponentInstance;
      if (!dc || !contextName) {
        return;
      }
      const orphaned = [];
      const stack = [dc.rootElementId].filter(id => id !== null && id !== undefined);
      while (stack.length) {
        const elementId = stack.pop();
        const element = DynamicElement.get(elementId);
        if (!element?.element) {
          continue;
        }
        element.childElementIds.forEach(childId => stack.push(childId));
        const required = this.readElementRequiredContexts(element);
        if (Object.prototype.hasOwnProperty.call(required, contextName)) {
          orphaned.push(element);
        }
      }
      if (orphaned.length) {
        this.deleteElements(orphaned);
      }
    },

    loadBrokenReferences(dynamicComponentId, publicationId) {
      const id = Number(dynamicComponentId) || 0;
      const pubId = Number(publicationId) || 0;
      if (!id || !pubId) {
        return Promise.resolve();
      }
      return this.apiGet(
        `/dynamic-component/${pubId}/broken-references/${id}`,
        {},
        response => {
          const broken = response?.brokenReferences;
          this.brokenReferencesByElementId = broken && typeof broken === "object" ? broken : {};
        },
        error => {
          // eslint-disable-next-line no-console
          console.error(
            `[DCE] could not load broken component references for DC ${id}; the canvas will not `
            + "flag broken elements this session, though the compile gate still refuses them.",
            error,
          );
          this.brokenReferencesByElementId = {};
        },
      );
    },

    readElementRequiredContexts(element) {
      const leaf = element.getRawOptionValue(REFERENCED_COMPONENT_ID_KEY, "default", "default");
      // XXX: The SAME binding the renderer reads -- the stored id, nothing else.
      // XXX: This used to fall back to a name lookup so untouched hosts kept
      // XXX: working; that fallback is gone (task #58), so an element with no
      // XXX: stored id has no contexts to report because it has no component.
      const referencedId = boundComponentIdFromStored(leaf?.r);
      if (referencedId > 0) {
        return readRequiredContexts(DynamicComponent.get(referencedId, false));
      }
      if (element.type === "DynamicComponentElement") {
        return {};
      }
      if (element.type === "DynamicAppComponentElement") {
        const context = element.parsedOptions?.context ?? {};
        const catalogEntry = window.blclient?.data?.dbApps?.[context.appName];
        return catalogEntry?.config?.components?.[context.componentName]?.requiredContexts ?? {};
      }
      return {};
    },

    deleteElements(elements) {
      const seen = new Set();
      const uniqueElements = elements.filter(element => {
        if (!element || seen.has(element.id)) {
          return false;
        }
        seen.add(element.id);
        return true;
      });
      uniqueElements.forEach(element => {
        this.dynamicComponentInstance?.removeRemovedElementEventsFromParentRootOptions(element.type);
      });
      this.dynamicComponentInstance?.reconcileRegisteredEvents(uniqueElements.map(element => element.id));
      this.triggerEvent("DeleteElements", { elements: uniqueElements.map(element => ({ id: element.id })) });
      const deletedIds = new Set(uniqueElements.map(element => String(element.id)));
      this.currentlySelectedElementIds = this.currentlySelectedElementIds
          .filter(qualifiedId => !deletedIds.has(String(qualifiedId).split(":")[0]));
      this.elementTreeMutationEpoch++;
    },

    duplicateElements(elements) {
      this.triggerEvent("DuplicateElements", { elements: elements.map(element => ({ id: element.id })) });
      this.elementTreeMutationEpoch++;
    },

    moveElements(elements, parentElement, previousElement) {
      this.triggerEvent("MoveElements", {
        elements: elements.map(element => ({ id: element.id })),
        parentElement: { id: parentElement.id },
        previousElement: previousElement ? { id: previousElement.id } : undefined,
      });
    },

    wrapElements(elements, containerType, parentElement, previousElement) {
      const dynamicComponent = elements[0]?.dynamicComponent?.pk;
      if (!dynamicComponent) {
        throw new DynamicComponentError("Cannot wrap elements: Target elements are not associated with a Dynamic Component.", { elements });
      }

      this.triggerEvent("WrapElements", {
        dynamicComponent,
        elements: elements.map(element => ({ id: element.id })),
        containerType,
        parentElement: { id: parentElement.id },
        previousElement: previousElement ? { id: previousElement.id } : undefined,
      });
      this.elementTreeMutationEpoch++;
    },

    wrapSelectionInContainer(containerType, identifiers = this.currentlySelectedElementIds) {
      if (!identifiers.length) {
        return;
      }
      const firstEl = DynamicElement.get(identifiers[0]);
      const parentId = firstEl.parentElementId;
      if (!parentId) {
        return;
      }

      const allShareParent = identifiers.every(id => DynamicElement.get(id)?.parentElementId === parentId);
      if (!allShareParent) {
        return;
      }

      const minIndex = Math.min(...identifiers.map(id => DynamicElement.get(id).indexInParent).filter(i => i >= 0));
      const previousSibling = DynamicElement.getSiblingAtIndex(parentId, minIndex - 1);

      if (previousSibling?.element?.isEntityPkTemp?.() === true) {
        const tempBareId = String(previousSibling.element.id);
        const stop = watch(
          () => DynamicElement.get(tempBareId)?.element?.isEntityPkTemp?.(),
          isStillTemp => {
            if (isStillTemp !== false) {
              return;
            }
            stop();
            this.wrapSelectionInContainer(containerType, identifiers);
          },
          { immediate: false },
        );
        setTimeout(stop, WRAP_RECONCILE_TIMEOUT_MS);
        return;
      }

      const elements = identifiers
          .map(id => DynamicElement.get(id))
          .filter(Boolean)
          .sort((a, b) => a.indexInParent - b.indexInParent)
          .map(dynamicElement => dynamicElement.element)
          .filter(Boolean);
      const parentElement = DynamicElement.get(parentId).element;
      this.wrapElements(elements, containerType, parentElement, previousSibling?.element);
    },

    async handleClipboardElementPaste() {
      const pasteTargetId = this.currentlySelectedElementIds[0];
      if (pasteTargetId && this.isElementTemp(pasteTargetId)) {
        return;
      }
      const items = await Clipboard.getFormattedClipboardItems();

      this._beginPasteFlow();
      await this._yieldForModalPaint();

      try {
        const unwrapped = items.map(item => {
          if (item && typeof item === "object" && item.type === PUBLICATION_CLIPBOARD_TYPE) {
            return item.contents;
          }
          return item;
        });
        const studio = App.get("CreatorStudio");
        studio.beginTransactionBatch();
        try {
          if (isLegacyElementPayload(unwrapped)) {
            this.pasteLegacyElementTree(unwrapped);
          } else {
            this.pasteElementTreeFromClipboard(items);
          }
        } finally {
          studio.endTransactionBatch();
        }
      } finally {
        this._endPasteFlowAfterFirstPaint();
      }
    },

    _beginPasteFlow() {
      this.isPastingElements = true;
    },
    _yieldForModalPaint() {
      return new Promise(resolve => {
        if (typeof requestAnimationFrame !== "function") {
          setTimeout(resolve, 0);
          return;
        }
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
    },
    _endPasteFlowAfterFirstPaint() {
      if (this._pasteDrainUnsubscribe) {
        this._pasteDrainUnsubscribe();
        this._pasteDrainUnsubscribe = null;
      }
      if (this._pasteSafetyTimeout) {
        clearTimeout(this._pasteSafetyTimeout);
        this._pasteSafetyTimeout = null;
      }

      const finish = () => {
        if (this._pasteDrainUnsubscribe) {
          this._pasteDrainUnsubscribe();
          this._pasteDrainUnsubscribe = null;
        }
        if (this._pasteSafetyTimeout) {
          clearTimeout(this._pasteSafetyTimeout);
          this._pasteSafetyTimeout = null;
        }
        this.isPastingElements = false;
      };

      if (pendingMountJobs() === 0) {
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(() => requestAnimationFrame(finish));
        } else {
          setTimeout(finish, 0);
        }
      } else {
        this._pasteDrainUnsubscribe = onDrain(finish);
      }

      this._pasteSafetyTimeout = setTimeout(finish, 15000);
    },

    pasteElementTreeFromClipboard(items) {
      const { parentElement, previousElement: initialPrevious } = this.resolvePasteTarget();
      let previousElement = initialPrevious;
      for (const item of items) {
        if (!item || item.type !== PUBLICATION_CLIPBOARD_TYPE || !item.contents) {
          continue;
        }
        const inserted = this.addSerializedElementSubtree(item.contents, parentElement, previousElement);
        if (inserted) {
          previousElement = inserted;
        }
      }
    },

    addSerializedElementSubtree(node, parentElement, previousElement) {
      if (!node || typeof node !== "object" || typeof node.type !== "string") {
        return undefined;
      }
      const expectedIndex = (previousElement?.indexInParent ?? -1) + 1;
      this.addElements(
        {
          type: node.type,
          displayName: node.displayName ?? "",
          options: node.options ?? "{}",
          context: parseContextBinding(node.options),
        },
        parentElement,
        previousElement,
      );

      const newChild = DynamicElement.getSiblingAtIndex(parentElement.id, expectedIndex);
      if (!newChild?.element) {
        return undefined;
      }

      const contents = Array.isArray(node.contents) ? node.contents : [];
      let lastChild;
      for (const childNode of contents) {
        lastChild = this.addSerializedElementSubtree(childNode, newChild.element, lastChild) ?? lastChild;
      }

      return newChild.element;
    },

    pasteLegacyElementTree(legacyNodes) {
      const nodes = Array.isArray(legacyNodes) ? legacyNodes : [legacyNodes];
      if (nodes.length === 0) {
        return;
      }
      const { parentElement, previousElement: initialPrevious } = this.resolvePasteTarget();
      let previousElement = initialPrevious;
      for (const legacyNode of nodes) {
        if (!isLegacyElementNode(legacyNode)) {
          continue;
        }
        previousElement = this.addLegacyElementSubtree(legacyNode, parentElement, previousElement)
          ?? previousElement;
      }
    },

    addLegacyElementSubtree(legacyNode, parentElement, previousElement) {
      const converted = convertLegacyElementNode(legacyNode, this.buildLegacyConversionContext());
      const expectedIndex = (previousElement?.indexInParent ?? -1) + 1;

      this.addElements(
        {
          type: converted.type,
          displayName: converted.displayName,
          options: converted.options,
          context: parseContextBinding(converted.options),
        },
        parentElement,
        previousElement,
      );

      const newChild = DynamicElement.getSiblingAtIndex(parentElement.id, expectedIndex);
      if (!newChild?.element) {
        return undefined;
      }

      let lastChild;
      for (const childNode of converted.contents) {
        if (!isLegacyElementNode(childNode)) {
          continue;
        }
        lastChild = this.addLegacyElementSubtree(childNode, newChild.element, lastChild) ?? lastChild;
      }

      return newChild.element;
    },

    resolvePasteTarget() {
      const selectedId = this.currentlySelectedElementIds[0];
      let targetId = selectedId || this.rootElementIdentifier;
      let previousElementId;
      if (selectedId) {
        const selectedEl = DynamicElement.get(selectedId);
        if (!DynamicElement.isElementOfTypeLayout(selectedEl.type)) {
          targetId = selectedEl.parentElementId ?? this.rootElementIdentifier;
          previousElementId = selectedId;
        }
      }
      const parentElement = DynamicElement.get(targetId).element;
      const previousElement = previousElementId ? DynamicElement.get(previousElementId).element : undefined;
      return { parentElement, previousElement };
    },

    buildLegacyConversionContext() {
      return {
        themeId: this.themeId,
        themeMode: this.themeMode,
        breakpoints: this.availableBreakpoints,
      };
    },

    handleOnDelete(identifier) {
      const idsToDelete = (identifier ? [identifier] : this.currentlySelectedElementIds).filter(id => this.isDeletable(id));
      if (idsToDelete.length) {
        const elements = idsToDelete.map(id => DynamicElement.get(id).element).filter(Boolean);
        this.deleteElements(elements);
      }
      this.clearSelectedElements();
    },

    handleDuplicate(identifier = this.currentlySelectedElementIds[0]) {
      if (!identifier || !this.isDuplicatable(identifier)) {
        return;
      }
      const element = DynamicElement.get(identifier).element;
      if (element) {
        this.duplicateElements([element]);
      }
    },

    handleDetach(identifier = this.currentlySelectedElementIds[0]) {
      const element = DynamicElement.get(identifier);
      if (element.element?.template?.componentId) {
        element.updateOption("template", this.activeBreakpointAlias, "default", null, {});
      }
      this.detachingElementIdentifier = null;
    },

    handleDragStart(element, event, isCustom = false, fromLayersPanel = false) {
      const elementIdentifier = typeof element === "string" ? element : element.identifier || element.id;
      const draggingElement = typeof element === "string" ? DynamicElement.get(elementIdentifier).element : element;

      const dragType = typeof isCustom === "string" ? isCustom : isCustom ? "custom" : "element";
      if (event) {
        event.dataTransfer.setData("text/plain", JSON.stringify({ type: dragType, element: draggingElement }));
        if (this.gpuOverlayActive) {
          const empty = this.buildEmptyDragImage();
          document.body.appendChild(empty);
          setDragImageUnlessRefused(event.dataTransfer, empty, 0, 0);
        }

        const domSelector = fromLayersPanel
          ? `[data-panel-element-identifier="${elementIdentifier}"]`
          : `[data-edit-identifier="${elementIdentifier}"], [data-identifier="${elementIdentifier}"]`;
        const domElement = document.querySelector(domSelector);
        if (domElement) {
          if (!this.gpuOverlayActive) {
            const rect = domElement.getBoundingClientRect();
            const label = this.dragPreviewLabel(draggingElement);
            const preview = this.buildDragPreviewElement(rect, label);
            document.body.appendChild(preview);
            setDragImageUnlessRefused(
              event.dataTransfer,
              preview,
              event.clientX - rect.left,
              event.clientY - rect.top,
            );
          }
          setTimeout(() => domElement.setAttribute("data-dragging-element", "true"), 10);
        }
      }

      this.isDragging = true;
      this.isDraggingOnLayersPalette = fromLayersPanel;
      this.currentlyDraggingElement = draggingElement;
      this.recomputeFocusSets();
    },

    dragPreviewLabel(element) {
      if (!element) {
        return "Element";
      }
      return element.displayName
        || element.name
        || element.type
        || element.elementPanelSettings?.displayName
        || "Element";
    },

    buildDragPreviewElement(rect, label) {
      const preview = document.createElement("div");
      preview.id = "dragging-clone";
      preview.style.cssText = [
        "position: fixed",
        "top: -10000px",
        "left: -10000px",
        `width: ${Math.max(rect.width, 24)}px`,
        `height: ${Math.max(rect.height, 24)}px`,
        "pointer-events: none",
        "z-index: 9999",
        "background-color: rgba(37, 99, 235, 0.25)",
        "border: 2px dashed rgba(37, 99, 235, 0.9)",
        "border-radius: 6px",
        "box-sizing: border-box",
        "display: flex",
        "align-items: center",
        "justify-content: center",
        "color: #1e3a8a",
        "font-family: system-ui, -apple-system, sans-serif",
        "font-size: 13px",
        "font-weight: 600",
        "padding: 8px",
        "overflow: hidden",
        "box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18)",
      ].join(";");
      preview.textContent = label;
      return preview;
    },

    buildEmptyDragImage() {
      const el = document.createElement("div");
      el.id = "dragging-clone";
      el.style.cssText = "position:fixed;top:-10000px;left:-10000px;width:1px;height:1px;opacity:0;pointer-events:none";
      return el;
    },

    toggleLayerCollapsed(identifier) {
      const next = { ...this.layersCollapsed };
      if (next[identifier]) {
        delete next[identifier];
      } else {
        next[identifier] = true;
      }
      this.layersCollapsed = next;
    },

    expandLayerAncestors(ancestorIdentifiers) {
      if (!ancestorIdentifiers || !ancestorIdentifiers.length) {
        return;
      }
      const next = { ...this.layersCollapsed };
      let changed = false;
      for (const id of ancestorIdentifiers) {
        if (next[id]) {
          delete next[id];
          changed = true;
        }
      }
      if (changed) {
        this.layersCollapsed = next;
      }
    },

    panToElementInCanvas(identifier) {
      const ic = this.canvasRef;
      if (!ic || typeof ic.setTransform !== "function") {
        return;
      }
      const move = typeof ic.animateToTransform === "function"
        ? (x, y, s) => ic.animateToTransform(x, y, s)
        : (x, y, s) => ic.setTransform(x, y, s);
      const region = ic.$el;
      const el = document.querySelector(`[data-edit-identifier="${identifier}"]`);
      if (!region || !el) {
        return;
      }
      const regionRect = region.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const t = ic.transform || { x: 0, y: 0, scale: 1 };
      const scale = t.scale || 1;
      const halfElW = elRect.width / 2;
      const halfElH = elRect.height / 2;
      const elCenterX = elRect.left + halfElW;
      const elCenterY = elRect.top + halfElH;
      const relX = elCenterX - regionRect.left - t.x;
      const relY = elCenterY - regionRect.top - t.y;
      const contentX = relX / scale;
      const contentY = relY / scale;
      const screenX = contentX * scale;
      const screenY = contentY * scale;
      const halfRegionW = regionRect.width / 2;
      const halfRegionH = regionRect.height / 2;
      const tx = halfRegionW - screenX;
      const ty = halfRegionH - screenY;
      move(tx, ty, scale);
    },

    handleDragEnd() {
      this.isDragging = false;
      this.isDraggingOnLayersPalette = false;
      this.currentlyDraggingElement = null;
      this.dropTarget = { containerIdentifier: null, index: 0 };
      this.dropTargetElementIdentifier = null;
      this.clearLayersHoverTimer();
      const clone = document.getElementById("dragging-clone");
      if (clone) {
        clone.remove();
      }
      this.resetDropIndicators();
      document.querySelectorAll("[data-dragging-element]").forEach(el => el.removeAttribute("data-dragging-element"));
      this.recomputeFocusSets();
    },

    isElementWithinSubtree(candidateId, ancestorId) {
      const target = String(ancestorId).split(":")[0];
      const visited = new Set();
      let cursor = String(candidateId).split(":")[0];
      while (cursor && !visited.has(cursor)) {
        if (cursor === target) {
          return true;
        }
        visited.add(cursor);
        const parent = DynamicElement.get(cursor)?.parentElementId;
        cursor = parent ? String(parent) : null;
      }
      return false;
    },

    addDroppedElement(elementConfig, parentElement, previousElement) {
      if (!elementConfig?.dropContents?.length) {
        return this.addElements(elementConfig, parentElement, previousElement);
      }
      const studio = App.get("CreatorStudio");
      studio.beginTransactionBatch();
      try {
        const droppedElementId = this.addElements(elementConfig, parentElement, previousElement);
        this.seedDroppedContents(droppedElementId, elementConfig.dropContents);
        return droppedElementId;
      } finally {
        studio.endTransactionBatch();
      }
    },

    insertElementAtSelection(paletteElement) {
      const { containerId, previousId } = clickInsertionPoint({
        selectedId: this.currentlySelectedElementIds[0] ?? null,
        rootId: this.pageRootElementId(),
        describe: id => {
          const element = DynamicElement.get(String(id));
          if (!element) {
            return null;
          }
          return {
            id: element.id,
            isContainer: DynamicElement.isElementOfTypeLayout(element.type),
            parentId: element.parentElementId,
            childIds: element.childElementIds,
          };
        },
      });
      const container = DynamicElement.get(String(containerId));
      const previousElement = previousId ? DynamicElement.get(String(previousId)).element : undefined;
      const addedId = this.addDroppedElement(paletteElement, container.element, previousElement);
      if (!addedId) {
        throw new DynamicComponentError("insertElementAtSelection: adding the clicked element answered no new element", {
          elementType: paletteElement?.type ?? paletteElement?.name ?? null,
          containerId: container.id,
        });
      }
      this.selectByEntityPk(addedId);
      return addedId;
    },

    insertLibrarySection(variantKey, parentElement, previousElement) {
      return useStudioSectionLibraryStore().fetchLibrarySectionTree(variantKey)
          .then(section => {
            const addedId = this.addLibrarySectionTree(section.tree, parentElement, previousElement);
            if (addedId) {
              this.selectByEntityPk(addedId);
            }
          })
          .catch(error => {
            // eslint-disable-next-line no-console
            console.error(`insertLibrarySection: section ${variantKey} was not added to element ${parentElement?.id} — ${error.message}`);
          });
    },

    addLibrarySectionTree(tree, parentElement, previousElement) {
      const dynamicComponent = parentElement?.dynamicComponent?.pk;
      if (!dynamicComponent) {
        throw new DynamicComponentError("Cannot add a library section: the parent has no Dynamic Component association.", { parentElement });
      }
      const section = libraryTreeForInsert(tree);
      const result = this.triggerEvent("AddElementTree", {
        dynamicComponent,
        parentElement: { id: parentElement.id },
        tree: section.tree,
        previousElement: previousElement ? { id: previousElement.id } : undefined,
      });
      this.elementTreeMutationEpoch++;
      for (const elementType of section.elementTypes) {
        this.dynamicComponentInstance?.mergeInAddedElementEventsToParentRootOptions(elementType);
      }
      return result?.createElement?.id ?? null;
    },

    insertLibrarySectionAtSelection(variantKey) {
      const root = DynamicElement.get(this.pageRootElementId());
      const selectedId = this.currentlySelectedElementIds[0];
      const afterId = selectedId
        ? this.topLevelAncestorId(selectedId)
        : root.childElementIds[root.childElementIds.length - 1];
      const after = afterId ? DynamicElement.get(afterId).element : undefined;
      return this.insertLibrarySection(variantKey, root.element, after);
    },

    pageRootElementId() {
      const rootId = this.dynamicComponentInstance?.rootElementId ?? this.rootElementIdentifier;
      if (!rootId) {
        throw new DynamicComponentError("pageRootElementId: the open page has no root element yet", {
          dynamicComponentId: this.dynamicComponentInstance?.id ?? null,
        });
      }
      return String(rootId);
    },

    topLevelAncestorId(elementId) {
      const rootId = this.pageRootElementId();
      let currentId = String(elementId);
      for (let depth = 0; depth < MAX_ANCESTOR_DEPTH; depth++) {
        const parentId = DynamicElement.get(currentId)?.parentElementId;
        if (!parentId || String(parentId) === rootId) {
          return currentId === rootId ? null : currentId;
        }
        currentId = String(parentId);
      }
      throw new DynamicComponentError("topLevelAncestorId: the element's parent chain never reaches the page root", { elementId, rootId });
    },

    handleDrop(eventData, customData = null) {
      if (!this.isDragging) {
        return;
      }
      let containerIdentifier = this.dropTarget.containerIdentifier;
      let dropIndex = this.dropTarget.index;
      let dragData;

      let dragDataError = null;
      if (eventData) {
        try {
          const nativeEvent = eventData.event ?? eventData;
          dragData = JSON.parse(nativeEvent.dataTransfer?.getData("text/plain"));
        } catch (error) {
          dragData = null;
          dragDataError = error;
        }
      }

      if (customData) {
        containerIdentifier = customData.containerIdentifier || containerIdentifier;
        dragData = { type: customData.type, element: customData.element };
      }

      if (!dragData) {
        this.handleDragEnd();
        throw new DynamicComponentError("handleDrop: the drop carried no element to add or move, so nothing was dropped", {
          containerIdentifier,
          dropIndex,
          reason: dragDataError?.message ?? "the drag data was empty",
        });
      }

      const containerId = this.getDropContainerIdentifier(containerIdentifier);
      if (!containerId) {
        this.handleDragEnd();
        if (dragData.type !== "element") {
          this.refuseDropWithNoContainer(dragData);
        }
        return;
      }
      const parentElement = DynamicElement.get(containerId).element;

      const siblingIds = DynamicElement.get(containerId).childElementIds;
      const previousSiblingId = dropIndex > 0 ? siblingIds[dropIndex - 1] : null;

      if (dragData.type === "custom" || dragData.type === "new") {
        const previousElement = previousSiblingId ? DynamicElement.get(previousSiblingId).element : undefined;
        this.addDroppedElement(dragData.element, parentElement, previousElement);
        const addedChildIds = DynamicElement.get(containerId).childElementIds;
        const addedId = addedChildIds[dropIndex];
        if (addedId) {
          this.selectByEntityPk(addedId);
        }
      } else if (dragData.type === LIBRARY_DRAG_TYPE) {
        const previousElement = previousSiblingId ? DynamicElement.get(previousSiblingId).element : undefined;
        this.insertLibrarySection(dragData.element.variantKey, parentElement, previousElement);
      } else if (dragData.type === "element") {
        const draggingElement = DynamicElement.get(dragData.element.identifier || dragData.element.id).element;
        if (draggingElement) {
          const draggingParentRef = draggingElement.parentElement;
          if (!draggingParentRef?.pk?.id) {
            throw new DynamicComponentError(
              "handleDrop: dragged element is missing parentElement — entity state is corrupt",
              { draggingElementId: draggingElement.id },
            );
          }
          const draggingOldParentId = String(draggingParentRef.pk.id);

          if (this.isElementWithinSubtree(containerId, draggingElement.id)) {
            this.handleDragEnd();
            return;
          }

          const draggingOwnVisualIndex = siblingIds.indexOf(String(draggingElement.id));
          const isSameParent = draggingOldParentId === String(containerId)
            && draggingOwnVisualIndex !== -1;
          if (isSameParent && (dropIndex === draggingOwnVisualIndex || dropIndex === draggingOwnVisualIndex + 1)) {
            this.handleDragEnd();
            return;
          }

          let effectivePrevSiblingId = previousSiblingId;
          if (effectivePrevSiblingId === String(draggingElement.id)) {
            effectivePrevSiblingId = dropIndex > 1 ? siblingIds[dropIndex - 2] : null;
          }
          const previousElement = effectivePrevSiblingId
            ? DynamicElement.get(effectivePrevSiblingId).element
            : undefined;

          this.moveElements([draggingElement], parentElement, previousElement);
          this.selectByEntityPk(draggingElement.id);
        }
      }
      this.handleDragEnd();
    },

    getOS() {
      return navigator.userAgent.toLowerCase().includes("win") ? "windows" : "mac";
    },

    handleKeyboardShortcuts(event) {
      if (event.key === "Escape" && (this.showContextualMenuForIdInBuilder || this.showContextualMenuForIdInLayers)) {
        this.closeContextualMenus();
        return;
      }

      const isInputFocused = ["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName) || document.activeElement?.contentEditable === "true";
      const isInDialog = !!document.activeElement?.closest?.("[role=\"dialog\"]");
      if (isInputFocused || isInDialog) {
        return;
      }
      if (document.querySelector(".modal-wrapper")) {
        return;
      }

      const isMeta = this.getOS() === "windows" ? event.ctrlKey : event.metaKey;

      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        this.handleOnDelete();
      } else if (event.key === "Escape") {
        this.clearSelectedElements();
      } else if (isMeta) {
        switch (event.key.toLowerCase()) {
          case "c":
            this.handleClipboardElementTransfer("copy");
            break;
          case "x":
            this.handleClipboardElementTransfer("cut");
            break;
          case "d":
            event.preventDefault();
            this.handleDuplicate();
            break;
          case "v":
            this.handleClipboardElementPaste();
            break;
          case "z":
            event.preventDefault();
            if (event.shiftKey) {
              this.dynamicComponentInstance?.redo();
            } else {
              this.dynamicComponentInstance?.undo();
            }
            break;
        }
      }
    },

    handleClipboardElementTransfer(action) {
      if (!this.currentlyInspectedElements.length) {
        return;
      }
      const serialized = this.currentlyInspectedElements
          .filter(item => !item?.isEntityPkTemp?.())
          .map(item => DynamicElement.serializeElementSubtree(item))
          .filter(Boolean);
      if (!serialized.length) {
        return;
      }
      Clipboard.copyToClipboard(serialized, action, PUBLICATION_CLIPBOARD_TYPE);
      if (action === "cut") {
        this.handleOnDelete();
      }
    },

    drillSelectedElements(direction) {
      if (!this.currentlySelectedElementIds.length) {
        return;
      }
      const newSelection = new Set();
      this.currentlyInspectedElements.forEach(element => {
        if (direction === "upward" && element.parentElementId) {
          newSelection.add(element.parentElementId);
        } else if (direction === "downward" && element.childElementIds?.length) {
          element.childElementIds.forEach(childId => newSelection.add(childId));
        }
      });
      if (newSelection.size > 0) {
        this.currentlySelectedElementIds = Array.from(newSelection);
        this.recomputeFocusSets();
      }
    },

    qualifyToActiveBreakpoint(identifier) {
      const alias = this.activeBreakpointAlias || "default";
      return `${String(identifier).split(":")[0]}:${alias}`;
    },

    assertSelectionSingleBreakpoint() {
      const aliases = new Set(this.currentlySelectedElementIds.map(id => breakpointAliasOf(id)));
      if (aliases.size > 1) {
        throw new SelectionBreakpointInvariantError("selection spans multiple breakpoints", {
          selectedIds: [...this.currentlySelectedElementIds],
          aliases: [...aliases],
        });
      }
    },

    handleElementSelection(identifier, multiSelect = false) {
      if (multiSelect) {
        const qualified = this.currentlySelectedElementIds.length
          ? this.qualifyToActiveBreakpoint(identifier)
          : identifier;
        const index = this.currentlySelectedElementIds.indexOf(qualified);
        index > -1 ? this.currentlySelectedElementIds.splice(index, 1) : this.currentlySelectedElementIds.push(qualified);
        this.selectionAnchorIdentifier = qualified;
      } else {
        this.currentlySelectedElementIds = [identifier];
        this.selectionAnchorIdentifier = identifier;
      }
      this.assertSelectionSingleBreakpoint();
      this.recomputeFocusSets();
    },

    buildElementSelectionOrder(breakpointAlias = null) {
      const rootElementId = this.dynamicComponentInstance?.rootElementId;
      if (!rootElementId) {
        return [];
      }
      const collapsed = this.layersCollapsed || {};
      return flattenVisibleIdentifiers({
        rootElementId,
        breakpointAlias: breakpointAlias || this.activeBreakpointAlias || "default",
        isCollapsed: identifier => Boolean(collapsed[identifier]),
        childIdsOf: (bareId, identifier) => {
          const qualified = DynamicElement.get(identifier);
          if (qualified) {
            return qualified.childElementIds;
          }
          const bare = DynamicElement.get(bareId);
          if (bare) {
            return bare.childElementIds;
          }
          return [];
        },
      });
    },

    selectElementRangeTo(identifier) {
      const anchorIdentifier = this.selectionAnchorIdentifier;
      const target = this.currentlySelectedElementIds.length
        ? this.qualifyToActiveBreakpoint(identifier)
        : identifier;
      const targetAlias = breakpointAliasOf(target);
      if (!anchorIdentifier
        || anchorIdentifier === target
        || breakpointAliasOf(anchorIdentifier) !== targetAlias) {
        this.handleElementSelection(target, false);
        return;
      }
      const range = identifiersBetween(
        this.buildElementSelectionOrder(targetAlias),
        anchorIdentifier,
        target,
      );
      if (!range.length) {
        this.handleElementSelection(target, false);
        return;
      }
      this.currentlySelectedElementIds = range;
      this.assertSelectionSingleBreakpoint();
      this.recomputeFocusSets();
    },

    selectByEntityPk(pk, multiSelect = false) {
      if (pk === undefined || pk === null) {
        return;
      }
      const alias = this.activeBreakpointAlias || "default";
      const bareId = String(pk);
      const initialQualified = `${bareId}:${alias}`;
      this.handleElementSelection(initialQualified, multiSelect);
      const wrapper = DynamicElement.get(bareId);
      const entity = wrapper?.element;
      if (!entity || entity.isEntityPkTemp?.() !== true) {
        return;
      }
      const stop = watch(
        () => DynamicElement.get(bareId)?.element?.isEntityPkTemp?.(),
        isStillTemp => {
          if (isStillTemp !== false) {
            return;
          }
          const realPk = DynamicElement.get(bareId)?.element?.id;
          if (realPk === undefined || realPk === null || String(realPk) === bareId) {
            stop();
            return;
          }
          const staleQualified = initialQualified;
          const freshQualified = `${realPk}:${alias}`;
          recordKeyAlias(realPk, bareId);
          const index = this.currentlySelectedElementIds.indexOf(staleQualified);
          if (index !== -1) {
            this.currentlySelectedElementIds.splice(index, 1, freshQualified);
            this.recomputeFocusSets();
          }
          stop();
        },
        { immediate: false },
      );
    },

    clearSelectedElements() {
      this.currentlySelectedElementIds = [];
      this.recomputeFocusSets();
    },

    restoreSelectionFromUrl() {
      const urlBreakpoint = readBreakpointUrlState();
      const validUrlBreakpoint = urlBreakpoint
        && this.selectableBreakpoints.some(bp => bp.alias === urlBreakpoint);
      if (this.currentlySelectedElementIds.length) {
        if (validUrlBreakpoint) {
          this.activeBreakpointAlias = urlBreakpoint;
        } else {
          const injectedAlias = breakpointAliasOf(this.currentlySelectedElementIds[0]);
          if (injectedAlias && this.selectableBreakpoints.some(bp => bp.alias === injectedAlias)) {
            this.activeBreakpointAlias = injectedAlias;
          }
        }
        this.requalifySelectionToActiveBreakpoint();
        return;
      }
      if (validUrlBreakpoint) {
        this.activeBreakpointAlias = urlBreakpoint;
      }
      const alias = this.activeBreakpointAlias || "default";
      const qualified = readSelectionUrlState()
          .filter(bareId => {
            const entity = DynamicElement.get(bareId)?.element;
            return entity && entity.isEntityPkTemp?.() !== true;
          })
          .map(bareId => `${bareId}:${alias}`);
      if (!qualified.length) {
        return;
      }
      this.currentlySelectedElementIds = qualified;
      this.recomputeFocusSets();
    },

    setElementToFocusOnHover(id) {
      this.currentlyHoveringOverElementId = id;
      this.recomputeFocusSets();
    },

    recomputeFocusSets() {
      const alias = this.activeBreakpointAlias || "default";
      const selected = this.currentlySelectedElementIds.map(id => `${String(id).split(":")[0]}:${alias}`);
      const hovered = this.currentlyHoveringOverElementId != null
        ? [String(this.currentlyHoveringOverElementId)]
        : [];
      const focusBare = new Set();
      for (const qualified of [...selected, ...hovered]) {
        focusBare.add(qualified.split(":")[0]);
      }
      const parentsBare = new Set();
      for (const bareId of focusBare) {
        const parentId = DynamicElement.get(bareId)?.parentElementId;
        if (parentId != null) {
          parentsBare.add(String(parentId));
        }
      }
      const draggingBare = this.draggingCanvasElementBareId;
      reconcileSet(this.selectionSet, selected);
      reconcileSet(this.hoverSet, hovered);
      reconcileSet(this.focusBareSet, focusBare);
      reconcileSet(this.parentsOfFocusBareSet, parentsBare);
      reconcileSet(this.draggingBareSet, draggingBare ? [draggingBare] : []);
      this.focusRevision++;
    },

    isDeletable(id) {
      const rootElementId = this.dynamicComponentInstance?.rootElementId ?? this.rootElementIdentifier;
      return id !== rootElementId && !this.isElementTemp(id) && (DynamicElement.get(id).element?.lock_options?.isDeletable ?? true);
    },

    isDuplicatable(id) {
      return id !== this.rootElementIdentifier && !this.isElementTemp(id) && (DynamicElement.get(id).element?.lock_options?.isDuplicatable ?? true);
    },

    isDraggable(id) {
      if (this.editingElementsInline.length > 0 || id === this.rootElementIdentifier || this.isElementTemp(id)) {
        return false;
      }
      return DynamicElement.get(id).element?.lock_options?.isDraggable ?? true;
    },

    canWrap(id) {
      return !this.isElementTemp(id) && (DynamicElement.get(id).element?.lock_options?.canWrap ?? true);
    },

    isElementTemp(id) {
      return DynamicElement.get(id)?.element?.isEntityPkTemp?.() === true;
    },

    refuseDropWithNoContainer(dragData) {
      const what = dragData.element?.displayName || dragData.element?.elementPanelSettings?.displayName || dragData.element?.name || "The element";
      console.warn(`[DCE] handleDrop: ${what} was dropped where no container is under the cursor, so it was not added`, { dragType: dragData.type }); // eslint-disable-line no-console
    },

    getDropContainerIdentifier(containerId) {
      return containerId === "builder-panel" ? this.rootElementIdentifier : containerId;
    },

    generateDragImageAndMousePosition(element, clientX, clientY) {
      const rect = element.getBoundingClientRect();
      const scale = this.zoom || 1;
      const container = document.createElement("div");
      container.id = "dragging-clone";
      container.style.cssText = [
        "position:fixed",
        "left:-10000px",
        "top:-10000px",
        `width:${rect.width}px`,
        `height:${rect.height}px`,
        "pointer-events:none",
        "z-index:9999",
        `zoom:${scale}`,
      ].join(";");
      const clone = element.cloneNode(true);
      clone.style.cssText = "opacity:0.4;position:absolute;top:0;left:0;box-shadow:0 4px 16px rgba(0,0,0,0.35);";
      const stripAttrs = ["data-drag-state", "data-edit-border", "data-has-override", "data-dragging-element"];
      const stripFrom = node => {
        if (node.nodeType === 1) {
          stripAttrs.forEach(attr => node.removeAttribute(attr));
          node.childNodes.forEach(stripFrom);
        }
      };
      stripFrom(clone);
      container.appendChild(clone);
      return {
        previewElement: container,
        xPositionOfMouseRelativeToElement: clientX - rect.left,
        yPositionOfMouseRelativeToElement: clientY - rect.top,
      };
    },

    resetDropIndicators() {
      document.querySelectorAll(".drop-indicator").forEach(el => el.remove());
    },

    clearLayersHoverTimer() {
      if (this.draggingOverHoverState.timerId) {
        clearTimeout(this.draggingOverHoverState.timerId);
      }
      this.draggingOverHoverState.targetId = null;
      this.draggingOverHoverState.timerId = null;
    },

    selectBreakpoint(aliasOrBreakpoint) {
      let alias;
      if (!aliasOrBreakpoint) {
        alias = "default";
      } else if (typeof aliasOrBreakpoint === "string") {
        alias = aliasOrBreakpoint;
      } else if (typeof aliasOrBreakpoint === "object" && typeof aliasOrBreakpoint.alias === "string") {
        alias = aliasOrBreakpoint.alias;
      } else {
        alias = "default";
      }
      this.activeBreakpointAlias = alias || "default";
      this.requalifySelectionToActiveBreakpoint();
    },

    setActiveBreakpointByAlias(aliasOrBreakpoint) {
      this.selectBreakpoint(aliasOrBreakpoint);
    },

    focusBreakpoint(aliasOrBreakpoint) {
      this.selectBreakpoint(aliasOrBreakpoint);
    },

    requalifySelectionToActiveBreakpoint() {
      if (!this.currentlySelectedElementIds.length) {
        return;
      }
      const alias = this.activeBreakpointAlias || "default";
      const requalified = this.currentlySelectedElementIds.map(id => `${String(id).split(":")[0]}:${alias}`);
      const changed = requalified.some((qualified, index) => qualified !== this.currentlySelectedElementIds[index]);
      if (!changed) {
        return;
      }
      this.currentlySelectedElementIds = requalified;
      if (this.selectionAnchorIdentifier) {
        this.selectionAnchorIdentifier = `${String(this.selectionAnchorIdentifier).split(":")[0]}:${alias}`;
      }
      this.recomputeFocusSets();
    },
    handleClipboardPublicationElementTransfer(...args) {
      return this.handleClipboardElementTransfer(...args);
    },
    handleClipboardPublicationElementPaste(...args) {
      return this.handleClipboardElementPaste(...args);
    },

    allowsDrop() {
      return true;
    },
    canCreateCustomComponent() {
      return false;
    },
    canWrapCurrentSelection() {
      return false;
    },
    clearElementBorder() {},
    toggleElementBorder() {},
    findElementInLayout() {
      return null;
    },
    generateComponentName() {
      return "";
    },
    generateMultiDragImageAndMousePosition() {
      return { previewElement: null, xPositionOfMouseRelativeToElement: 0, yPositionOfMouseRelativeToElement: 0 };
    },
    getElement(id) {
      return DynamicElement.get(id)?.element ?? null;
    },
    getLayoutOfElementByIdentifier() {
      return null;
    },

    handleDragLeaveForElement() {},
    handleDragLeaveForLayout() {},
    handleDragOverForLayout() {},
    handleElementDragStart(...args) {
      return this.handleDragStart(...args);
    },
    handleMultiElementDragStart(...args) {
      return this.handleDragStart(...args);
    },
    handleOnEdit() {},
    handleOnEditParent() {},

    isEditable() {
      return true;
    },
    isEditingElementInline(id) {
      return this.editingElementsInline.includes(id);
    },
    isElementTemplatedComponent() {
      return false;
    },
    setEditorElementConfig() {},
    setElementAsEditingInline(id) {
      if (!this.editingElementsInline.includes(id)) {
        this.editingElementsInline.push(id);
      }
    },
    removeElementFromEditingInline(id) {
      this.editingElementsInline = this.editingElementsInline.filter(entry => entry !== id);
    },
    setShowContextualMenuForIdInBuilder(id) {
      this.showContextualMenuForIdInBuilder = id;
    },
    setShowContextualMenuForIdInLayers(id) {
      this.showContextualMenuForIdInLayers = id;
    },
    closeContextualMenus() {
      this.showContextualMenuForIdInBuilder = null;
      this.showContextualMenuForIdInLayers = null;
      this.contextualMenuPosition = { top: 0, left: 0 };
    },
    wrapElementInContainer(containerType, identifier) {
      this.wrapSelectionInContainer(containerType, identifier ? [identifier] : undefined);
    },
  },
});

function isLegacyElementNode(value) {
  return Boolean(
    value
    && typeof value === "object"
    && !Array.isArray(value)
    && value.elementType === "dynamic"
    && typeof value.type === "string",
  );
}

function isLegacyElementPayload(value) {
  if (Array.isArray(value)) {
    return value.length > 0 && value.every(isLegacyElementNode);
  }
  return isLegacyElementNode(value);
}

const LEGACY_OPTION_KEY_PATTERN = /^([^:.]*)(?::([^.]+))?\.(.+)$/;

const LEGACY_ELEMENT_TYPE_MAP = {
  VerticalContainer: "VerticalContainerWrapper",
  HorizontalContainer: "HorizontalContainerWrapper",
  Container: "VerticalContainerWrapper",
};

const LEGACY_OPTION_KEY_REMAP = {
  "vertical-layout-align": {
    key: "layout-align",
    values: { left: "start", center: "center", right: "end" },
  },
  "vertical-layout-vertical-align": {
    key: "layout-align-vertical",
    values: { top: "start", middle: "center", bottom: "end" },
  },
  "horizontal-layout-align": {
    key: "layout-align",
    values: { left: "start", center: "center", right: "end" },
  },
  "horizontal-layout-vertical-align": {
    key: "layout-align-vertical",
    values: { top: "start", middle: "center", bottom: "end" },
  },
};

function parseLegacyOptionKey(legacyKey) {
  const match = LEGACY_OPTION_KEY_PATTERN.exec(legacyKey);
  if (!match) {
    return null;
  }
  const [, breakpointRaw, stateRaw, optionKey] = match;
  return {
    breakpoint: breakpointRaw || "default",
    state: stateRaw || "default",
    optionKey,
  };
}

function convertLegacyOptionsTree(legacyOptions, elementType, conversionContext) {
  if (!legacyOptions || typeof legacyOptions !== "object" || Array.isArray(legacyOptions)) {
    return {};
  }
  const elementData = { type: elementType };
  const elementDef = DynamicElement.getElementDefinition(elementType);
  const BrandingClass = elementDef?.brandingType ?? null;
  const isThemableForElement = BrandingClass && typeof BrandingClass.isThemableOptionKey === "function"
    ? optionKey => BrandingClass.isThemableOptionKey(optionKey)
    : () => false;

  const tree = {};
  for (const [legacyKey, rawValue] of Object.entries(legacyOptions)) {
    const parsed = parseLegacyOptionKey(legacyKey);
    if (!parsed) {
      continue;
    }
    const { breakpoint, state, optionKey: parsedOptionKey } = parsed;
    const remap = LEGACY_OPTION_KEY_REMAP[parsedOptionKey];
    const optionKey = remap?.key ?? parsedOptionKey;
    const remappedValue = remap?.values && typeof rawValue === "string"
      ? remap.values[rawValue] ?? rawValue
      : rawValue;

    const valueIsThemeRef = remappedValue !== null
      && typeof remappedValue === "object"
      && typeof remappedValue?.source === "string"
      && remappedValue.source.startsWith("theme.");
    if (valueIsThemeRef && isThemableForElement(optionKey)) {
      continue;
    }

    for (const themeMode of ["light-mode", "dark-mode"]) {
      const leaf = DynamicOption.processElementOption(
        elementData, optionKey, breakpoint, state, remappedValue, themeMode, conversionContext,
      );
      if (!leaf) {
        continue;
      }
      tree[themeMode] = tree[themeMode] || {};
      tree[themeMode][breakpoint] = tree[themeMode][breakpoint] || {};
      tree[themeMode][breakpoint][state] = tree[themeMode][breakpoint][state] || {};
      tree[themeMode][breakpoint][state][optionKey] = leaf;
    }
  }
  return tree;
}

function convertLegacyElementNode(legacyNode, conversionContext) {
  const newType = LEGACY_ELEMENT_TYPE_MAP[legacyNode.type] ?? legacyNode.type;
  return {
    type: newType,
    displayName: typeof legacyNode.displayName === "string" ? legacyNode.displayName : "",
    options: convertLegacyOptionsTree(legacyNode.options, newType, conversionContext),
    contents: Array.isArray(legacyNode.contents) ? legacyNode.contents : [],
  };
}

function mergeDefaultsWithOverrides(defaults, overrides) {
  const out = { ...defaults };
  for (const themeMode of Object.keys(overrides)) {
    const overrideBreakpoints = overrides[themeMode];
    if (!overrideBreakpoints || typeof overrideBreakpoints !== "object") {
      continue;
    }
    const defaultBreakpoints = out[themeMode] || {};
    const mergedBreakpoints = { ...defaultBreakpoints };
    for (const breakpoint of Object.keys(overrideBreakpoints)) {
      const overrideStates = overrideBreakpoints[breakpoint];
      if (!overrideStates || typeof overrideStates !== "object") {
        continue;
      }
      const defaultStates = defaultBreakpoints[breakpoint] || {};
      const mergedStates = { ...defaultStates };
      for (const state of Object.keys(overrideStates)) {
        const overrideOptions = overrideStates[state];
        if (!overrideOptions || typeof overrideOptions !== "object") {
          continue;
        }
        mergedStates[state] = { ...defaultStates[state] || {}, ...overrideOptions };
      }
      mergedBreakpoints[breakpoint] = mergedStates;
    }
    out[themeMode] = mergedBreakpoints;
  }
  return out;
}

registerBuilderPageReader(() => useDynamicComponentEditorStore().expressionBuilderPage);

export default useDynamicComponentEditorStore;

